import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Image,
  ScrollView,
  Platform,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";

import { FASTAPI_BASE_URL, JOURNEY_ASSET_BASE_URL } from "../../config";
import {
  getInstructionalFlowModes,
  getInstructionalModeMeta,
  localizeInstructionalMode,
  localizeInstructionalModes,
} from "./journeyInstructionalModes";

import JourneyStoryPrelude from "./JourneyStoryPrelude";
import { getMasterTeacherMediaCue, getMasterTeacherWatchSpec } from "./masterTeacherMediaCues";
import { getMasterTeacherPhysicalBriefing } from "./masterTeacherPhysicalCues";
import { getMasterTeacherKitGuide } from "./masterTeacherKitGuides";
import WonderLabsProgressNavigator from "./present/WonderLabsProgressNavigator";
import { buildGroundedPhaseRecap } from "./journeyGroundedRecap";
import { buildSwanActivityContext, swanActivityDomId } from "./journeySwanActivityContext";
import { getJourneyFocusGroups } from "./journeyFocusLayout";
import { isFloodRescueWholeJourneyEnabled as isWonderLabsActivityFirstEnabled } from "./floodRescueWholeJourney";
import { WONDERLABS_FLOW, getWonderLabsActivityState } from "./wonderLabsWholeJourneys";
import { startActivityFromBeginning } from "./wonderLabsReviewNavigation";
import {
  isWonderLabsLearningBeatsEnabled, resolveLearningBeats, beatCanAdvance, beatProgress,
  learningBeatRequirement,
} from "./wonderLabsLearningBeats";
import {
  FLOOD_FAIR_TEST_ACTIVITIES,
  getFloodFairTestStatus,
} from "./floodRescueActivityFirst";
import { ZoomableImage } from "./ImageLightbox";

// Lets deeply nested blocks translate fixed UI text without threading `t` through
// every block component. Defaults to identity so blocks render stand-alone.
const JourneyTContext = createContext((value) => value);
const MissionFocusedContext = createContext(false);
const useJourneyT = () => useContext(JourneyTContext);

const getJourneyAssetUrl = (imagePath) => {
  const cleanPath = String(imagePath || "").replace(/^\/+/, "");
  return `${JOURNEY_ASSET_BASE_URL}/${cleanPath}`;
};

const getStepState = (sessionState, stepId) => {
  const steps =
    sessionState && typeof sessionState.steps === "object"
      ? sessionState.steps
      : {};

  const value = steps?.[stepId];
  return value && typeof value === "object" ? value : {};
};

const resolveValue = (currentValue, nextValue) =>
  typeof nextValue === "function"
    ? nextValue(currentValue)
    : nextValue;

const normalizeInstructionalMode = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const getRuleTargetBlock = (rule, page) =>
  page?.blocks?.find(
    (block) => block.save_as === rule?.field
  );

const hashStableSeed = (value) => {
  let hash = 2166136261;
  const textValue = String(value || "");

  for (
    let index = 0;
    index < textValue.length;
    index += 1
  ) {
    hash ^= textValue.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const stableShuffle = (items, seed) => {
  const shuffled = Array.isArray(items) ? [...items] : [];

  if (shuffled.length < 2) return shuffled;

  let state = hashStableSeed(seed) || 0x9e3779b9;

  for (
    let index = shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;

    const swapIndex = (state >>> 0) % (index + 1);

    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

// Only the starting choice order changes for WonderLabs R2 sequence builders.
// Keep the authored answer key, immediate feedback and completion gates intact.
const getSequencePresentationItems = (items, block, r2Enabled) => {
  if (!r2Enabled || items.length < 2) return items;

  const shuffled = stableShuffle(
    items,
    `sequence:${block.id || block.save_as || ""}`
  );
  const expected =
    Array.isArray(block.correct_order) &&
    block.correct_order.length === items.length
      ? block.correct_order
      : items.map((item) => item.id);

  // Even when the shuffle happens to match the key, rotate once.
  return shuffled.every((item, index) => item.id === expected[index])
    ? [...shuffled.slice(1), shuffled[0]]
    : shuffled;
};

const ruleIsComplete = (rule, stepState, page) => {
  const targetBlock = getRuleTargetBlock(rule, page);

  const instructionalMode = normalizeInstructionalMode(
    targetBlock?.instructional_mode
  );

  if (
    !targetBlock?.self_guided_active &&
    (instructionalMode === "I_DO" ||
      instructionalMode === "WE_DO")
  ) {
    return true;
  }

  const value = stepState?.[rule.field];

  switch (rule.condition) {
    case "truthy":
      return Boolean(value);

    case "answered":
      return (
        value !== null &&
        value !== undefined &&
        String(value).trim().length > 0
      );

    case "min_length":
      return String(value || "").trim().length >= Number(rule.value || 0);

    case "min_items":
      return (
        Array.isArray(value) &&
        value.length >= Number(rule.value || 0)
      );

    case "completed": {
      if (!value || typeof value !== "object") return false;

      const classificationBlock = page?.blocks?.find(
        (block) => block.save_as === rule.field
      );

      const requiredItemIds = Array.isArray(classificationBlock?.items)
        ? classificationBlock.items.map((item) => item.id)
        : [];

      return (
        requiredItemIds.length > 0 &&
        requiredItemIds.every((itemId) => Boolean(value[itemId]))
      );
    }

    case "sequence_correct": {
      const sequenceBlock = page?.blocks?.find(
        (block) => block.save_as === rule.field
      );

      const correctOrder = Array.isArray(sequenceBlock?.correct_order)
        ? sequenceBlock.correct_order
        : [];

      return (
        Array.isArray(value) &&
        value.length === correctOrder.length &&
        value.every((itemId, index) => itemId === correctOrder[index])
      );
    }

    case "equals":
      return value === rule.value;

    case "all_equal": {
      if (!value || typeof value !== "object") return false;

      const targetBlock = page?.blocks?.find(
        (block) => block.save_as === rule.field
      );

      const requiredIds = Array.isArray(targetBlock?.items)
        ? targetBlock.items.map((item) => item.id)
        : [];

      return (
        requiredIds.length > 0 &&
        requiredIds.every((itemId) => value[itemId] === rule.value)
      );
    }

    case "record_complete": {
      if (!value || typeof value !== "object") return false;

      const recordBlock = page?.blocks?.find(
        (block) => block.save_as === rule.field
      );

      const requiredFields = Array.isArray(recordBlock?.fields)
        ? recordBlock.fields.filter((field) => field.required !== false)
        : [];

      return (
        requiredFields.length > 0 &&
        requiredFields.every((field) => {
          const fieldValue = value[field.id];

          if (field.type === "text") {
            return (
              String(fieldValue || "").trim().length >=
              Number(field.min_length || 1)
            );
          }

          return (
            fieldValue !== null &&
            fieldValue !== undefined &&
            String(fieldValue).trim().length > 0
          );
        })
      );
    }

    default:
      return false;
  }
};


const getCompletionGate = (page) =>
  (page?.blocks || []).find((block) => block.type === "completion_gate") || null;

const getPageCompletionState = (page, stepState) => {
  const gate = getCompletionGate(page);
  const rules = Array.isArray(gate?.rules) ? gate.rules : [];

  const items = rules.map((rule) => {
    const targetBlock = getRuleTargetBlock(rule, page);
    const complete = ruleIsComplete(rule, stepState, page);
    const label =
      page?.self_guided_progress_labels?.[rule.field] ||
      targetBlock?.self_guided_progress_label ||
      targetBlock?.title ||
      rule.field;

    return {
      field: rule.field,
      label,
      complete,
      targetBlockId: targetBlock?.id || null,
    };
  });

  const completed = items.filter((item) => item.complete).length;

  return {
    hasGate: rules.length > 0,
    allComplete: rules.length > 0 && completed === rules.length,
    completed,
    total: rules.length,
    items,
    firstIncomplete: items.find((item) => !item.complete) || null,
  };
};

const getSelfGuidedVisibleBlocks = (page, stepState) => {
  const blocks = Array.isArray(page?.blocks) ? page.blocks : [];

  if (!page?.self_guided_progressive) return blocks;

  const completion = getPageCompletionState(page, stepState);
  if (!completion.hasGate) return blocks;

  const gate = getCompletionGate(page);

  if (completion.allComplete) {
    return blocks.filter((block) => block.type !== "saras_checkpoint");
  }

  const firstIncompleteId = completion.firstIncomplete?.targetBlockId;
  const firstIncompleteIndex = blocks.findIndex(
    (block) => block.id === firstIncompleteId
  );

  if (firstIncompleteIndex < 0) return blocks;

  return blocks.filter((block, index) => {
    if (block === gate) return true;
    if (block.type === "saras_checkpoint") return false;
    return index <= firstIncompleteIndex;
  });
};

const getChildFriendlyResponseHint = ({ value, complete }) => {
  const text = String(value || "").trim();

  if (complete) return null;
  if (!text) return null;
  return "Tell us a little more about your idea.";
};

const buildActivityFromBlock = (block) => {
  const asset = block?.asset || {};
  const assetRef = asset?.asset_ref || {};
  const context = asset?.content_context || {};

  return {
    activity_id: asset?.id || block?.id || null,
    type:
      asset?.type === "saras_video"
        ? "video"
        : asset?.type === "saras_pwo"
          ? "pwo"
          : asset?.type || null,
    title: block?.title || asset?.title || null,
    description: block?.mission || null,
    asset: {
      ...assetRef,
      source_topic_id: context?.source_topic_id ?? null,
      board_topic_id: context?.board_topic_id ?? null,
    },
    provenance: asset?.provenance || null,
  };
};


const getInstructionalModePalette = (mode, c) => {
  const palette = c.instructionalModes?.[mode?.value];

  return (
    palette || {
      accent: c.accent,
      soft: c.accentSoft,
      border: c.border,
    }
  );
};

const SELF_GUIDED_MODE_META = {
  I_DO: { icon: "🦢", actionLabel: "Professor Swan teaches" },
  WE_DO: { icon: "🤝", actionLabel: "Work with Professor Swan" },
  YOU_DO: { icon: "✍️", actionLabel: "Your turn" },
};

const getDisplayedInstructionalMode = (mode, selfGuided) => {
  if (!mode || !selfGuided) return mode;
  const override = SELF_GUIDED_MODE_META?.[mode.value];
  return override ? { ...mode, ...override } : mode;
};

function InstructionalModeStrip({ block, c }) {
  const t = useJourneyT();
  const focused = useContext(MissionFocusedContext);
  if (focused) return null;
  const localizedMode = localizeInstructionalMode(
    getInstructionalModeMeta(block?.instructional_mode),
    t
  );
  const mode = getDisplayedInstructionalMode(
    localizedMode,
    Boolean(block?.self_guided_active)
  );

  if (!mode) return null;

  if (
    block?.self_guided_active &&
    ["I_DO", "WE_DO"].includes(mode.value)
  ) {
    return null;
  }

  const palette = getInstructionalModePalette(mode, c);
  const studentCue = t(String(block?.student_cue || "").trim());
  // A self-guided YOU DO card repeats the activity prompt and DO THIS NOW
  // guidance on every question. Keep authored safety/detail text available,
  // but render it as quiet inline guidance rather than a second large banner.
  if (block?.self_guided_active && mode.value === "YOU_DO") {
    const generic = /^(your turn[.! ]*)?((choose|write|explain|record|answer|select) your (own |best )?(answer|idea|prediction|explanation)|ask professor swan for a hint)/i;
    const important = /safety|adult|supervis|unplug|power off|battery|sharp|hot|caution|warning|never |do not /i;
    const showCue = studentCue && (!generic.test(studentCue) || important.test(studentCue));
    return showCue ? (
      <Text style={{ color: c.muted, fontSize: 13, lineHeight: 19, marginBottom: 8 }}>
        {studentCue}
      </Text>
    ) : null;
  }
  const accessibilityText = [
    mode.label,
    mode.actionLabel,
    studentCue,
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <View
      accessible
      accessibilityRole="text"
      accessibilityLabel={accessibilityText}
      style={{
        marginBottom: 14,
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 12,
        borderWidth: 1,
        borderLeftWidth: 5,
        borderColor: palette.border,
        borderLeftColor: palette.accent,
        backgroundColor: palette.soft,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 7,
        }}
      >
        <Text style={{ fontSize: 17 }}>{mode.icon}</Text>
        <Text
          style={{
            color: palette.accent,
            fontSize: 13,
            fontWeight: "900",
            letterSpacing: 0.7,
          }}
        >
          {mode.label}
        </Text>
        <Text
          style={{
            color: c.muted,
            fontSize: 13,
            fontWeight: "800",
          }}
        >
          {"—"} {mode.actionLabel}
        </Text>
      </View>

      {!!studentCue && (
        <Text
          style={{
            color: c.text,
            fontSize: 14,
            lineHeight: 20,
            fontWeight: "700",
            marginTop: 6,
          }}
        >
          {studentCue}
        </Text>
      )}
    </View>
  );
}

function InstructionalFlowSummary({ flow, c }) {
  const t = useJourneyT();
  const selfGuided = Boolean(flow?.self_guided_active);
  const modes = localizeInstructionalModes(
    getInstructionalFlowModes(flow),
    t
  ).map((mode) => getDisplayedInstructionalMode(mode, selfGuided));

  if (!modes.length) return null;

  const accessibilityLabel = `${t("This step")}: ${modes
    .map((mode) => `${mode.label}, ${mode.actionLabel}`)
    .join(`; ${t("then")} `)}`;

  return (
    <View
      accessible
      accessibilityRole="text"
      accessibilityLabel={accessibilityLabel}
      style={{
        marginTop: 18,
        padding: 14,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: c.border,
        backgroundColor: c.panel,
      }}
    >
      <Text
        style={{
          color: c.muted,
          fontSize: 11,
          fontWeight: "900",
          letterSpacing: 0.8,
        }}
      >
        {t("THIS STEP")}
      </Text>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginTop: 9,
        }}
      >
        {modes.map((mode, index) => {
          const palette = getInstructionalModePalette(mode, c);

          return (
            <React.Fragment key={mode.value}>
              {index > 0 && (
                <Text
                  style={{
                    color: c.muted,
                    fontSize: 16,
                    fontWeight: "900",
                  }}
                >
                  →
                </Text>
              )}

              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 6,
                  paddingHorizontal: 10,
                  paddingVertical: 7,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: palette.border,
                  backgroundColor: palette.soft,
                }}
              >
                <Text style={{ fontSize: 15 }}>{mode.icon}</Text>
                <Text
                  style={{
                    color: palette.accent,
                    fontSize: 12,
                    fontWeight: "900",
                  }}
                >
                  {mode.label}
                </Text>
                <Text
                  style={{
                    color: c.text,
                    fontSize: 12,
                    fontWeight: "700",
                  }}
                >
                  {mode.actionLabel}
                </Text>
              </View>
            </React.Fragment>
          );
        })}
      </View>
    </View>
  );
}

function ProfessorSwanTeachingMoment({ block, c }) {
  const t = useJourneyT();
  const focused = useContext(MissionFocusedContext);

  if (focused || !block?.self_guided_active) return null;

  const localizedMode = localizeInstructionalMode(
    getInstructionalModeMeta(block?.instructional_mode),
    t
  );
  const mode = getDisplayedInstructionalMode(localizedMode, true);
  const guidance = block?.self_guided_guidance;

  if (!mode || !guidance || !["I_DO", "WE_DO"].includes(mode.value)) {
    return null;
  }

  const palette = getInstructionalModePalette(mode, c);
  const isModel = mode.value === "I_DO";
  const heading = isModel
    ? t("PROFESSOR SWAN · LET ME SHOW YOU")
    : t("PROFESSOR SWAN · LET'S DO THIS TOGETHER");

  return (
    <View
      style={{
        marginBottom: 14,
        padding: 14,
        borderRadius: 13,
        borderWidth: 1,
        borderColor: palette.border,
        backgroundColor: palette.soft,
      }}
    >
      <Text
        style={{
          color: palette.accent,
          fontSize: 11,
          fontWeight: "900",
          letterSpacing: 0.7,
        }}
      >
        {heading}
      </Text>

      {!!guidance?.title && (
        <Text
          style={{
            color: c.text,
            fontSize: 17,
            lineHeight: 23,
            fontWeight: "900",
            marginTop: 5,
          }}
        >
          {t(guidance.title)}
        </Text>
      )}

      {!!guidance?.say && (
        <Text
          style={{
            color: c.text,
            fontSize: 14,
            lineHeight: 21,
            marginTop: 6,
          }}
        >
          {t(guidance.say)}
        </Text>
      )}

      {!!guidance?.example && (
        <View
          style={{
            marginTop: 9,
            padding: 10,
            borderRadius: 10,
            backgroundColor: c.card,
            borderWidth: 1,
            borderColor: palette.border,
          }}
        >
          <Text style={{ color: palette.accent, fontSize: 11, fontWeight: "900" }}>
            {isModel ? t("MY EXAMPLE") : t("CHECK WITH ME")}
          </Text>
          <Text style={{ color: c.text, fontSize: 14, lineHeight: 20, marginTop: 3 }}>
            {t(guidance.example)}
          </Text>
        </View>
      )}

      {!!guidance?.try_now && (
        <Text
          style={{
            color: c.text,
            fontSize: 14,
            lineHeight: 20,
            fontWeight: "800",
            marginTop: 9,
          }}
        >
          {"→ "}{t(guidance.try_now)}
        </Text>
      )}
    </View>
  );
}

function BlockShell({
  block,
  children,
  c,
  title,
  prompt,
  eyebrow,
  tone = "default",
}) {
  const background =
    tone === "mystery"
      ? c.mysterySoft
      : tone === "ai"
        ? c.ai
        : c.card;

  const border =
    tone === "mystery"
      ? c.mysteryBorder
      : tone === "ai"
        ? c.aiBorder
        : c.border;

  return (
    <View
      nativeID={block?.id ? swanActivityDomId(block.id) : undefined}
      style={{
        marginTop: 20,
        padding: 18,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: border,
        backgroundColor: background,
      }}
    >
      <InstructionalModeStrip block={block} c={c} />
      <ProfessorSwanTeachingMoment block={block} c={c} />

      {!!eyebrow && (
        <Text
          style={{
            color: c.mystery,
            fontSize: 12,
            fontWeight: "900",
            letterSpacing: 1.1,
            marginBottom: 7,
          }}
        >
          {eyebrow}
        </Text>
      )}

      {!!title && (
        <Text
          style={{
            color: c.text,
            fontSize: 20,
            lineHeight: 26,
            fontWeight: "900",
          }}
        >
          {title}
        </Text>
      )}

      {!!prompt && (
        <Text
          style={{
            color: c.muted,
            fontSize: 15,
            lineHeight: 22,
            marginTop: 7,
          }}
        >
          {prompt}
        </Text>
      )}

      <View style={{ marginTop: title || prompt || eyebrow ? 15 : 0 }}>
        {children}
      </View>
    </View>
  );
}


function ProfessorSwanHelpPrompt({
  checkpoint,
  c,
  contextPayload,
  onOpenHelp,
}) {
  const t = useJourneyT();

  if (!checkpoint?.self_guided_active || !onOpenHelp) return null;

  return (
    <TouchableOpacity
      onPress={() => onOpenHelp?.(contextPayload)}
      accessibilityRole="button"
      accessibilityLabel={t("Need help? Ask Professor Swan")}
      activeOpacity={0.84}
      style={{
        marginTop: 16,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: 13,
        borderWidth: 1,
        borderColor: c.aiBorder,
        backgroundColor: c.ai,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <View style={{ flex: 1 }}>
        <Text
          style={{
            color: c.mystery,
            fontSize: 11,
            fontWeight: "900",
            letterSpacing: 0.7,
          }}
        >
          {t("NEED HELP?")}
        </Text>
        <Text
          style={{
            color: c.text,
            fontSize: 15,
            lineHeight: 21,
            fontWeight: "800",
            marginTop: 2,
          }}
        >
          {t("Ask Professor Swan about this part")}
        </Text>
      </View>
      <Text style={{ color: c.link, fontSize: 18, fontWeight: "900" }}>→</Text>
    </TouchableOpacity>
  );
}

function StoryHookBlock({ block, c, onReadAloud }) {
  return (
    <BlockShell
      block={block}
      c={c}
      eyebrow={block.eyebrow}
      title={block.title}
      tone={block.tone}
    >
      <View style={{ gap: 12 }}>
        <MasterTeacherPhysicalBriefing block={block} c={c} onReadAloud={onReadAloud} />
        <Text style={{ color: c.text, fontSize: 16, lineHeight: 25 }}>
          {block.body}
        </Text>
      </View>
    </BlockShell>
  );
}

function ImageRevealBlock({
  block,
  c,
  value,
  setField,
  readOnly = false,
  t,
}) {
  const revealed = Boolean(value);

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      {!revealed ? (
        <>
        <Text style={{ color: c.text, fontSize: 14, lineHeight: 21, marginBottom: 12 }}>
          {t("This is a hidden picture clue, not a 3D model. Select the button below to uncover the image, then compare it with your prediction.")}
        </Text>
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={t("Reveal hidden picture clue")}
          onPress={() => { if (!readOnly) setField(block.save_as, true); }}
          disabled={readOnly}
          activeOpacity={0.85}
          style={{
            minHeight: 190,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: c.mysteryBorder,
            backgroundColor: c.mysterySoft,
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <Text style={{ fontSize: 46 }}>🔎</Text>
          <Text
            style={{
              color: c.mystery,
              fontSize: 18,
              fontWeight: "900",
              marginTop: 12,
            }}
          >
            {t("Tap here to reveal the picture clue")}
          </Text>
        </TouchableOpacity>
        </>
      ) : (
        <>
          <ZoomableImage
            uri={getJourneyAssetUrl(block.image?.path)}
            alt={block.image?.alt}
            t={t}
            imageStyle={{
              width: "100%",
              height: 360,
              borderRadius: 14,
              backgroundColor: c.panel,
            }}
          />

          <View
            style={{
              marginTop: 12,
              padding: 13,
              borderRadius: 12,
              backgroundColor: c.accentSoft,
              borderWidth: 1,
              borderColor: c.border,
            }}
          >
            <Text style={{ color: c.text, fontSize: 15, lineHeight: 22 }}>
              {t(
                block.reveal_text ||
                  "The looping artwork was created by a small machine—but the case is not over. Now you must discover how its parts work together."
              )}
            </Text>
          </View>
        </>
      )}
    </BlockShell>
  );
}

function ChoiceCardsBlock({
  block,
  c,
  value,
  setField,
  readOnly = false,
  multi = false,
}) {
  const selectedIds = multi && Array.isArray(value) ? value : [];

  const selectChoice = (choiceId) => {
    if (!multi) {
      setField(block.save_as, choiceId);
      return;
    }

    setField(block.save_as, (current) => {
      const safe = Array.isArray(current) ? current : [];

      return safe.includes(choiceId)
        ? safe.filter((item) => item !== choiceId)
        : [...safe, choiceId];
    });
  };

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <View style={{ gap: 10 }}>
        {(block.choices || []).map((choice) => {
          const selected = multi
            ? selectedIds.includes(choice.id)
            : value === choice.id;

          return (
            <TouchableOpacity
              key={choice.id}
              onPress={() => { if (!readOnly) selectChoice(choice.id); }}
              disabled={readOnly}
              activeOpacity={0.8}
              style={{
                paddingVertical: 13,
                paddingHorizontal: 14,
                borderRadius: 12,
                borderWidth: selected ? 2 : 1,
                borderColor: selected ? c.accent : c.border,
                backgroundColor: selected ? c.accentSoft : c.input,
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Text
                style={{
                  color: selected ? c.accent : c.muted,
                  fontSize: 18,
                  fontWeight: "900",
                }}
              >
                {multi
                  ? selected
                    ? "☑"
                    : "☐"
                  : selected
                    ? "●"
                    : "○"}
              </Text>

              <Text
                style={{
                  flex: 1,
                  color: c.text,
                  fontSize: 15,
                  lineHeight: 22,
                  fontWeight: selected ? "800" : "600",
                }}
              >
                {choice.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </BlockShell>
  );
}

// Keep high-frequency typing local instead of rebuilding every Journey phase,
// coach context and persistence payload on each keypress. Flush on blur,
// navigation/unmount, and after a short idle; no evidence is discarded.
function BufferedJourneyTextInput({ value, onCommit, onBlur, ...rest }) {
  const [draft, setDraft] = useState(() => String(value ?? ""));
  const draftRef = useRef(draft);
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const flush = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    commitRef.current(draftRef.current);
  };

  useEffect(() => {
    if (dirtyRef.current) return;
    const next = String(value ?? "");
    if (next !== draftRef.current) {
      draftRef.current = next;
      setDraft(next);
    }
  }, [value]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (dirtyRef.current) commitRef.current(draftRef.current);
  }, []);

  return (
    <TextInput
      {...rest}
      value={draft}
      onChangeText={(text) => {
        draftRef.current = text;
        dirtyRef.current = true;
        setDraft(text);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, 260);
      }}
      onBlur={(event) => { flush(); onBlur?.(event); }}
    />
  );
}

function EvidenceResponseBlock({
  block,
  c,
  value,
  setField,
  readOnly = false,
  showValidation = false,
}) {
  const length = String(value || "").trim().length;
  const minimum = Number(block.min_length || 0);
  const selfGuided = Boolean(block?.self_guided_active);
  const complete = minimum <= 0 || length >= minimum;
  const childHint = getChildFriendlyResponseHint({
    value,
    minimum,
    complete,
  });

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <BufferedJourneyTextInput
        value={String(value || "")}
        onCommit={(text) => setField(block.save_as, text)}
        placeholder={block.placeholder}
        placeholderTextColor={c.placeholder}
        editable={!readOnly}
        multiline
        style={{
          minHeight: 110,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: showValidation && !complete ? "#e11d48" : c.border,
          backgroundColor: c.input,
          color: c.text,
          fontSize: 15,
          lineHeight: 22,
          padding: 14,
          textAlignVertical: "top",
        }}
      />

      {showValidation && !complete ? (
        <Text style={{ marginTop: 7, color: "#be123c", fontSize: 13, fontWeight: "800" }}>
          {`Please add your explanation in the outlined box (${length} of ${minimum} characters so far).`}
        </Text>
      ) : null}
      {minimum > 0 && (!selfGuided || (length > 0 && !complete)) && (
        <Text
          style={{
            color: complete ? c.success : selfGuided ? "#be123c" : c.muted,
            fontSize: selfGuided ? 13 : 12,
            lineHeight: selfGuided ? 19 : undefined,
            marginTop: 8,
            textAlign: selfGuided ? "left" : "right",
            fontWeight: selfGuided ? "700" : undefined,
          }}
        >
          {selfGuided
            ? childHint
            : complete
              ? "Evidence recorded"
              : `${Math.max(0, minimum - length)} more characters suggested`}
        </Text>
      )}
    </BlockShell>
  );
}

function ClassificationBlock({
  block,
  c,
  value,
  setField,
  readOnly = false,
}) {
  const selections =
    value && typeof value === "object" ? value : {};

  const choose = (itemId, categoryId) => {
    setField(block.save_as, (current) => ({
      ...(current && typeof current === "object" ? current : {}),
      [itemId]: categoryId,
    }));
  };

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <View style={{ gap: 13 }}>
        {(block.items || []).map((item) => {
          const selectedCategory = selections[item.id];

          return (
            <View
              key={item.id}
              style={{
                padding: 14,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: c.border,
                backgroundColor: c.input,
              }}
            >
              <Text
                style={{
                  color: c.text,
                  fontSize: 15,
                  lineHeight: 22,
                  fontWeight: "700",
                }}
              >
                {item.label}
              </Text>

              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 11,
                }}
              >
                {(block.categories || []).map((category) => {
                  const selected =
                    selectedCategory === category.id;

                  return (
                    <TouchableOpacity
                      key={category.id}
                      onPress={() => { if (!readOnly) choose(item.id, category.id); }}
                      disabled={readOnly}
                      style={{
                        paddingVertical: 8,
                        paddingHorizontal: 11,
                        borderRadius: 999,
                        borderWidth: selected ? 2 : 1,
                        borderColor: selected ? c.accent : c.border,
                        backgroundColor: selected
                          ? c.accentSoft
                          : c.card,
                      }}
                    >
                      <Text
                        style={{
                          color: selected ? c.accent : c.text,
                          fontSize: 13,
                          fontWeight: "800",
                        }}
                      >
                        {category.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          );
        })}
      </View>
    </BlockShell>
  );
}


function ImageGalleryBlock({
  block,
  c,
  value,
  visited = [],
  setField,
  readOnly = false,
}) {
  const t = useJourneyT();
  const focused = useContext(MissionFocusedContext);
  const guidedGallery = focused && block.id === "final-chain-image";
  // Informational image galleries have no save_as key. Keep their selection
  // local; only authored evidence galleries should write Journey state.
  const [localSelectedId, setLocalSelectedId] = useState(null);
  const selectedId = block.save_as ? value : localSelectedId;
  const seen = new Set([...(Array.isArray(visited) ? visited : []), ...(selectedId ? [selectedId] : [])]);
  const selectImage = (imageId) => {
    if (!block.save_as) {
      setLocalSelectedId(imageId);
      return;
    }
    if (readOnly) return;
    setField(block.save_as, imageId);
    if (guidedGallery) {
      setField(`${block.save_as}_viewed`, (previous) => {
        const prior = Array.isArray(previous) ? previous : [];
        return Array.from(new Set([...prior, ...(value ? [value] : []), imageId]));
      });
    }
  };
  const images = Array.isArray(block.images) ? block.images : [];
  const selected =
    images.find((image) => image.id === selectedId) ||
    images[0] ||
    null;

  if (!selected) return null;

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <View
        style={{
          borderRadius: 14,
          borderWidth: 1,
          borderColor: c.border,
          backgroundColor: c.panel,
          overflow: "hidden",
        }}
      >
        <ZoomableImage
          uri={getJourneyAssetUrl(selected.path)}
          alt={selected.alt || selected.caption}
          t={t}
          imageStyle={{
            width: "100%",
            height: 390,
            backgroundColor: c.panel,
          }}
        />

        {!!selected.caption && (
          <View
            style={{
              paddingVertical: 11,
              paddingHorizontal: 13,
              borderTopWidth: 1,
              borderTopColor: c.border,
              backgroundColor: c.card,
            }}
          >
            <Text
              style={{
                color: c.text,
                fontSize: 14,
                lineHeight: 21,
                fontWeight: "700",
              }}
            >
              {selected.caption}
            </Text>
          </View>
        )}
      </View>

      {images.length > 1 ? <Text style={{ color: c.accent, fontWeight: "800", marginTop: 11 }}>
        {guidedGallery
          ? `${t("Compare all three pictures to see the difference between the battery-powered rover and outdoor wind.")} ${seen.size} ${t("of")} ${images.length} ${t("viewed")}.`
          : t("Explore the pictures below. Tap each one to see its evidence, then return to your activity.")}
      </Text> : null}
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 10,
          marginTop: 12,
        }}
      >
        {images.map((image) => {
          const active = image.id === selected.id;

          return (
            <TouchableOpacity
              key={image.id}
              onPress={() => selectImage(image.id)}
              disabled={readOnly}
              activeOpacity={0.8}
              style={{
                width: "31%",
                minWidth: 150,
                borderRadius: 12,
                borderWidth: active ? 2 : 1,
                borderColor: active ? c.accent : c.border,
                backgroundColor: active ? c.accentSoft : c.input,
                overflow: "hidden",
              }}
            >
              <Image
                source={{ uri: getJourneyAssetUrl(image.path) }}
                accessibilityLabel={image.alt || image.caption}
                resizeMode="contain"
                style={{
                  width: "100%",
                  height: 120,
                  backgroundColor: c.panel,
                }}
              />

              <Text
                numberOfLines={2}
                style={{
                  color: active ? c.accent : c.text,
                  fontSize: 12,
                  lineHeight: 17,
                  fontWeight: "800",
                  paddingVertical: 8,
                  paddingHorizontal: 9,
                }}
              >
                {guidedGallery && seen.has(image.id) ? "✓ " : ""}{image.label || image.caption || t("Evidence image")}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </BlockShell>
  );
}

function MatchingBlock({
  block,
  c,
  value,
  setField,
  readOnly = false,
}) {
  const selections =
    value && typeof value === "object" ? value : {};

  const choose = (itemId, optionId) => {
    setField(block.save_as, (current) => ({
      ...(current && typeof current === "object" ? current : {}),
      [itemId]: optionId,
    }));
  };

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <View style={{ gap: 14 }}>
        {(block.items || []).map((item) => {
          const selectedOption = selections[item.id];

          return (
            <View
              key={item.id}
              style={{
                padding: 14,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: c.border,
                backgroundColor: c.input,
              }}
            >
              <Text
                style={{
                  color: c.text,
                  fontSize: 16,
                  lineHeight: 22,
                  fontWeight: "900",
                }}
              >
                {item.label}
              </Text>

              {!!item.prompt && (
                <Text
                  style={{
                    color: c.muted,
                    fontSize: 13,
                    lineHeight: 19,
                    marginTop: 4,
                  }}
                >
                  {item.prompt}
                </Text>
              )}

              <View style={{ gap: 8, marginTop: 11 }}>
                {stableShuffle(
                  item.options || [],
                  `matching:${block.id || block.save_as || ""}:${item.id || ""}`
                ).map((option) => {
                  const selected = selectedOption === option.id;

                  return (
                    <TouchableOpacity
                      key={option.id}
                      onPress={() => { if (!readOnly) choose(item.id, option.id); }}
                      disabled={readOnly}
                      activeOpacity={0.8}
                      style={{
                        paddingVertical: 10,
                        paddingHorizontal: 12,
                        borderRadius: 10,
                        borderWidth: selected ? 2 : 1,
                        borderColor: selected ? c.accent : c.border,
                        backgroundColor: selected
                          ? c.accentSoft
                          : c.card,
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 9,
                      }}
                    >
                      <Text
                        style={{
                          color: selected ? c.accent : c.muted,
                          fontSize: 17,
                          fontWeight: "900",
                        }}
                      >
                        {selected ? "●" : "○"}
                      </Text>

                      <Text
                        style={{
                          flex: 1,
                          color: c.text,
                          fontSize: 14,
                          lineHeight: 20,
                          fontWeight: selected ? "800" : "600",
                        }}
                      >
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          );
        })}
      </View>
    </BlockShell>
  );
}


function SequenceBuilderBlock({
  block,
  c,
  value,
  setField,
  readOnly = false,
  t,
  r2Shuffle = false,
}) {
  const sequence = Array.isArray(value) ? value : [];
  const items = Array.isArray(block.items) ? block.items : [];
  const correctOrder = Array.isArray(block.correct_order)
    ? block.correct_order
    : [];

  const itemMap = Object.fromEntries(
    items.map((item) => [item.id, item])
  );

  // PR #402 shuffles WonderLabs R2 sequences. PR #403 also opts in
  // specific Explorers sequences outside R2. Use one deterministic shuffle.
  const availableItems = getSequencePresentationItems(
    items,
    block,
    r2Shuffle || Boolean(block.shuffle_items)
  );
  const remaining = availableItems.filter(
    (item) => !sequence.includes(item.id)
  );

  const addItem = (itemId) => {
    setField(block.save_as, (current) => {
      const safe = Array.isArray(current) ? current : [];
      return safe.includes(itemId) ? safe : [...safe, itemId];
    });
  };

  const removeItem = (itemId) => {
    setField(block.save_as, (current) => {
      const safe = Array.isArray(current) ? current : [];
      return safe.filter((id) => id !== itemId);
    });
  };

  const moveItem = (index, direction) => {
    if (readOnly) return;
    setField(block.save_as, (current) => {
      const result = Array.isArray(current) ? [...current] : [];
      const destination = index + direction;
      if (destination < 0 || destination >= result.length) return result;
      [result[index], result[destination]] = [result[destination], result[index]];
      return result;
    });
  };
  const reset = () => setField(block.save_as, []);

  const complete =
    sequence.length === items.length && items.length > 0;

  const correct =
    complete &&
    correctOrder.length === sequence.length &&
    sequence.every(
      (itemId, index) => itemId === correctOrder[index]
    );

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <Text
        style={{
          color: c.muted,
          fontSize: 13,
          lineHeight: 19,
          marginBottom: 9,
        }}
      >
        {t("Build your sequence. Use Move up / Move down to rearrange it without starting over.")}
      </Text>

      <View style={{ gap: 8 }}>
        {sequence.map((itemId, index) => {
          const item = itemMap[itemId];

          return (
            <View
              key={`${itemId}-${index}`}
              style={{
                paddingVertical: 11,
                paddingHorizontal: 13,
                borderRadius: 11,
                borderWidth: 1,
                borderColor: c.accent,
                backgroundColor: c.accentSoft,
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
              }}
            >
              <View
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  backgroundColor: c.accent,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    color: "#ffffff",
                    fontSize: 13,
                    fontWeight: "900",
                  }}
                >
                  {index + 1}
                </Text>
              </View>

              <Text
                style={{
                  flex: 1,
                  color: c.text,
                  fontSize: 15,
                  lineHeight: 21,
                  fontWeight: "800",
                }}
              >
                {item?.label || itemId}
              </Text>

              <View style={{ flexDirection: "row", gap: 6 }}>
                {[
                  { delta: -1, label: "↑", hint: "Move up" },
                  { delta: 1, label: "↓", hint: "Move down" },
                ].map(({ delta, label, hint }) => (
                  <TouchableOpacity key={delta} accessibilityRole="button"
                    accessibilityLabel={`${t(hint)}: ${item?.label || itemId}`}
                    disabled={readOnly || index + delta < 0 || index + delta >= sequence.length}
                    onPress={() => moveItem(index, delta)}
                    style={{ paddingHorizontal: 8, paddingVertical: 6,
                      borderRadius: 7, borderWidth: 1, borderColor: c.border }}>
                    <Text style={{ color: c.text, fontWeight: "900" }}>{label}</Text>
                  </TouchableOpacity>
                ))}
                <TouchableOpacity accessibilityRole="button" disabled={readOnly}
                  onPress={() => removeItem(itemId)} style={{ padding: 7 }}>
                  <Text style={{ color: c.muted, fontSize: 12 }}>{t("Remove")}</Text>
                </TouchableOpacity>
              </View>
            </View>
          );
        })}
      </View>

      {remaining.length > 0 && (
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            marginTop: 12,
          }}
        >
          {remaining.map((item) => (
            <TouchableOpacity
              key={item.id}
              onPress={() => { if (!readOnly) addItem(item.id); }}
              disabled={readOnly}
              activeOpacity={0.8}
              style={{
                paddingVertical: 9,
                paddingHorizontal: 11,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: c.border,
                backgroundColor: c.input,
              }}
            >
              <Text
                style={{
                  color: c.text,
                  fontSize: 13,
                  lineHeight: 18,
                  fontWeight: "700",
                }}
              >
                + {item.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {sequence.length > 0 && (
        <TouchableOpacity
          onPress={() => { if (!readOnly) reset(); }}
          disabled={readOnly}
          style={{
            alignSelf: "flex-start",
            marginTop: 12,
            paddingVertical: 7,
            paddingHorizontal: 10,
          }}
        >
          <Text
            style={{
              color: c.link,
              fontSize: 13,
              fontWeight: "800",
            }}
          >
            {t("Reset sequence")}
          </Text>
        </TouchableOpacity>
      )}

      {complete && (
        <View
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 11,
            borderWidth: 1,
            borderColor: correct ? c.success : c.mysteryBorder,
            backgroundColor: correct ? c.successSoft : c.mysterySoft,
          }}
        >
          <Text
            style={{
              color: correct ? c.success : c.mystery,
              fontSize: 14,
              lineHeight: 20,
              fontWeight: "800",
            }}
          >
            {correct
              ? block.correct_feedback ||
                t("Your energy pathway is ready.")
              : block.retry_feedback ||
                t("Revisit the evidence and try a different order.")}
          </Text>
        </View>
      )}
    </BlockShell>
  );
}

function QuizQuestionBlock({
  block,
  c,
  value,
  setField,
  readOnly = false,
}) {
  const answered =
    value !== null &&
    value !== undefined &&
    String(value).trim().length > 0;

  const correct = answered && value === block.correct_answer;

  const displayChoices = stableShuffle(
    block.choices || [],
    `quiz:${block.id || block.save_as || block.title || ""}`
  );

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <View style={{ gap: 9 }}>
        {displayChoices.map((choice) => {
          const selected = value === choice.id;

          return (
            <TouchableOpacity
              key={choice.id}
              onPress={() => { if (!readOnly) setField(block.save_as, choice.id); }}
              disabled={readOnly}
              activeOpacity={0.8}
              style={{
                paddingVertical: 12,
                paddingHorizontal: 13,
                borderRadius: 11,
                borderWidth: selected ? 2 : 1,
                borderColor: selected ? c.accent : c.border,
                backgroundColor: selected ? c.accentSoft : c.input,
                flexDirection: "row",
                alignItems: "center",
                gap: 9,
              }}
            >
              <Text
                style={{
                  color: selected ? c.accent : c.muted,
                  fontSize: 17,
                  fontWeight: "900",
                }}
              >
                {selected ? "●" : "○"}
              </Text>

              <Text
                style={{
                  flex: 1,
                  color: c.text,
                  fontSize: 15,
                  lineHeight: 21,
                  fontWeight: selected ? "800" : "600",
                }}
              >
                {choice.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {answered && block.show_feedback !== false && (
        <View
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 11,
            borderWidth: 1,
            borderColor: correct ? c.success : c.mysteryBorder,
            backgroundColor: correct ? c.successSoft : c.mysterySoft,
          }}
        >
          <Text
            style={{
              color: correct ? c.success : c.mystery,
              fontSize: 14,
              lineHeight: 20,
              fontWeight: "900",
            }}
          >
            {correct ? "✓ Correct" : "Keep investigating"}
          </Text>

          <Text
            style={{
              color: c.text,
              fontSize: 14,
              lineHeight: 21,
              marginTop: 5,
            }}
          >
            {correct
              ? block.correct_feedback
              : block.incorrect_feedback}
          </Text>
        </View>
      )}
    </BlockShell>
  );
}


// Shared, opt-in spoken briefing at the exact physical-action beat.
// Professor Swan's existing voice hook triggers once per focused beat;
// this button lets a child hear the same instructions again.
function MasterTeacherPhysicalBriefing({ block, c, onReadAloud }) {
  const briefing = block?.master_teacher_briefing;
  if (!block?.self_guided_active || !briefing) return null;
  const say = () => {
    const words = briefing.spoken || (briefing.steps || []).join(". ");
    if (onReadAloud) onReadAloud(words);
    else if (Platform.OS === "web" && typeof window !== "undefined" &&
      window.speechSynthesis && window.SpeechSynthesisUtterance) {
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(new window.SpeechSynthesisUtterance(words));
    }
  };
  return (
    <View style={{ backgroundColor: c.accentSoft, borderColor: c.accent,
      borderWidth: 2, borderRadius: 14, padding: 18, gap: 10 }}>
      <Text style={{ color: c.text, fontSize: 19, fontWeight: "900" }}>
        {briefing.title || "What to do next"}
      </Text>
      {(briefing.steps || []).map((text, index) => (
        <Text key={index} style={{ color: c.text, fontSize: 17, lineHeight: 25,
          fontWeight: "700" }}>{`${index + 1}. ${text}`}</Text>
      ))}
      <TouchableOpacity accessibilityRole="button" onPress={say}
        style={{ alignSelf: "flex-start", borderRadius: 10,
          paddingVertical: 11, paddingHorizontal: 15, backgroundColor: c.accent }}>
        <Text style={{ color: "#fff", fontSize: 15, fontWeight: "900" }}>
          🔊 Hear Swan read these steps
        </Text>
      </TouchableOpacity>
    </View>
  );
}

function BuildChecklistBlock({
  block,
  c,
  value,
  setField,
  contextPayload,
  onAsk,
  onReadAloud,
  readOnly = false,
}) {
  const selections =
    value && typeof value === "object" ? value : {};

  const statuses = Array.isArray(block.statuses)
    ? block.statuses
    : [
        { id: "complete", label: "Completed" },
        { id: "help", label: "I need help" },
        { id: "not_yet", label: "Not yet" },
      ];

  const chooseStatus = (item, statusId) => {
    // Keep the authored completion state intact: asking for help is not
    // proof that a physical check has been completed.
    setField(block.save_as, (current) => ({
      ...(current && typeof current === "object" ? current : {}),
      [item.id]: statusId,
    }));

    // In student self-study mode, Help is an action, not just a status.
    // Use the same Professor Swan conversation as the rest of the Journey.
    // Teacher-led mode retains its existing status-only behavior.
    if (statusId !== "help" || !block.self_guided_active || !onAsk) return;

    const question = [
      `I'm working on the ${contextPayload?.step_title || "current mission"}, in the checklist "${block.title}".`,
      `I need help with this exact item: "${item.label}".`,
      item.help ? `The instructions say: ${item.help}` : "",
      "Please help me understand what to check, why it matters, and how to use my kit guide. Guide me one step at a time. Do not assume you can see my kit or mark this item completed for me.",
    ]
      .filter(Boolean)
      .join(" ");

    onAsk(question, {
      ...contextPayload,
      active_activity: {
        type: "build_checklist",
        block_id: block.id,
        block_title: block.title,
        checklist_item_id: item.id,
        checklist_item_label: item.label,
        checklist_item_help: item.help || null,
        student_status: "help",
      },
      student_work: {
        ...(contextPayload?.student_work || {}),
        [block.save_as]: {
          ...selections,
          [item.id]: "help",
        },
      },
    });
  };

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <View style={{ gap: 13 }}>
        <MasterTeacherPhysicalBriefing block={block} c={c} onReadAloud={onReadAloud} />
        {(block.items || []).map((item, index) => {
          const selectedStatus = selections[item.id];

          return (
            <View
              key={item.id}
              style={{
                padding: 14,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: c.border,
                backgroundColor: c.input,
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  gap: 11,
                }}
              >
                <View
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 15,
                    backgroundColor: c.accentSoft,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Text
                    style={{
                      color: c.accent,
                      fontWeight: "900",
                    }}
                  >
                    {index + 1}
                  </Text>
                </View>

                <View style={{ flex: 1 }}>
                  <Text
                    style={{
                      color: c.text,
                      fontSize: 15,
                      lineHeight: 22,
                      fontWeight: "800",
                    }}
                  >
                    {item.label}
                  </Text>

                  {!!item.help && (
                    <Text
                      style={{
                        color: c.muted,
                        fontSize: 13,
                        lineHeight: 19,
                        marginTop: 4,
                      }}
                    >
                      {item.help}
                    </Text>
                  )}
                </View>
              </View>

              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                {statuses.map((status) => {
                  const selected = selectedStatus === status.id;

                  return (
                    <TouchableOpacity
                      key={status.id}
                      onPress={() => { if (!readOnly) chooseStatus(item, status.id); }}
                      disabled={readOnly}
                      style={{
                        paddingVertical: 8,
                        paddingHorizontal: 11,
                        borderRadius: 999,
                        borderWidth: selected ? 2 : 1,
                        borderColor: selected ? c.accent : c.border,
                        backgroundColor: selected
                          ? c.accentSoft
                          : c.card,
                      }}
                    >
                      <Text
                        style={{
                          color: selected ? c.accent : c.text,
                          fontSize: 13,
                          fontWeight: "800",
                        }}
                      >
                        {status.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          );
        })}
      </View>
    </BlockShell>
  );
}

function ExperimentRecordBlock({
  block,
  c,
  value,
  setField,
  onReadAloud,
  readOnly = false,
}) {
  const record =
    value && typeof value === "object" ? value : {};

  const setRecordField = (fieldId, nextValue) => {
    setField(block.save_as, (current) => ({
      ...(current && typeof current === "object" ? current : {}),
      [fieldId]: nextValue,
    }));
  };

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <View style={{ gap: 15 }}>
        <MasterTeacherPhysicalBriefing block={block} c={c} onReadAloud={onReadAloud} />
        {(block.fields || []).map((field) => {
          const fieldValue = record[field.id];
          const minLength = Number(field.min_length || 0);
          const typedLength = String(fieldValue || "").trim().length;
          const selfGuided = Boolean(block?.self_guided_active);
          const complete =
            field.type !== "text" ||
            minLength <= 1 ||
            typedLength >= minLength;
          const showLengthHint =
            !readOnly &&
            field.type === "text" &&
            minLength > 1 &&
            typedLength > 0 &&
            !complete;
          const childHint = getChildFriendlyResponseHint({
            value: fieldValue,
            minimum: minLength,
            complete,
          });

          return (
            <View key={field.id}>
              <Text
                style={{
                  color: c.text,
                  fontSize: 15,
                  lineHeight: 22,
                  fontWeight: "800",
                  marginBottom: 8,
                }}
              >
                {field.label}
              </Text>

              {field.type === "text" ? (
                <>
                  <BufferedJourneyTextInput
                    value={String(fieldValue || "")}
                    onCommit={(text) => setRecordField(field.id, text)}
                    placeholder={field.placeholder}
                    placeholderTextColor={c.placeholder}
                    editable={!readOnly}
                    multiline
                    style={{
                      minHeight: field.multiline === false ? 48 : 92,
                      borderRadius: 12,
                      borderWidth: 1,
                      borderColor: c.border,
                      backgroundColor: c.input,
                      color: c.text,
                      fontSize: 15,
                      lineHeight: 22,
                      padding: 13,
                      textAlignVertical: "top",
                    }}
                  />
                  {showLengthHint ? (
                    <Text
                      style={{
                        color: selfGuided ? "#be123c" : c.muted,
                        fontSize: selfGuided ? 13 : 12,
                        lineHeight: selfGuided ? 19 : undefined,
                        fontWeight: "800",
                        marginTop: 6,
                      }}
                    >
                      {selfGuided
                        ? childHint
                        : `Keep going — ${minLength - typedLength} more ${
                            minLength - typedLength === 1
                              ? "character"
                              : "characters"
                          } needed`}
                    </Text>
                  ) : null}
                </>
              ) : (
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  {(field.choices || []).map((choice) => {
                    const selected = fieldValue === choice.id;

                    return (
                      <TouchableOpacity
                        key={choice.id}
                        onPress={() => {
                          if (!readOnly) setRecordField(field.id, choice.id);
                        }}
                        disabled={readOnly}
                        style={{
                          paddingVertical: 9,
                          paddingHorizontal: 12,
                          borderRadius: 999,
                          borderWidth: selected ? 2 : 1,
                          borderColor: selected
                            ? c.accent
                            : c.border,
                          backgroundColor: selected
                            ? c.accentSoft
                            : c.card,
                        }}
                      >
                        <Text
                          style={{
                            color: selected ? c.accent : c.text,
                            fontSize: 13,
                            fontWeight: "800",
                          }}
                        >
                          {choice.label}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </BlockShell>
  );
}


const readNestedValue = (source, path) =>
  String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce(
      (current, key) =>
        current !== null && current !== undefined
          ? current[key]
          : undefined,
      source
    );

const formatPriorValue = (value, item, t = (text) => text) => {
  if (value === null || value === undefined || value === "") {
    return item.empty_text || t("Not recorded yet");
  }

  if (item.value_labels && item.value_labels[value]) {
    return item.value_labels[value];
  }

  if (Array.isArray(value)) {
    return value
      .map((entry) => item.value_labels?.[entry] || entry)
      .join(", ");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, entry]) => {
        const keyLabel = item.field_labels?.[key] || key;
        const entryLabel = item.value_labels?.[entry] || entry;
        return `${keyLabel}: ${entryLabel}`;
      })
      .join(" • ");
  }

  return String(value);
};

function PriorWorkSummaryBlock({
  block,
  c,
  sessionState,
}) {
  const t = useJourneyT();
  const steps =
    sessionState && typeof sessionState.steps === "object"
      ? sessionState.steps
      : {};

  return (
    <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
      <View style={{ gap: 10 }}>
        {(block.items || []).map((item) => {
          const sourceStep =
            steps[item.source_step_id] &&
            typeof steps[item.source_step_id] === "object"
              ? steps[item.source_step_id]
              : {};

          const value = readNestedValue(sourceStep, item.field);
          const formatted = formatPriorValue(value, item, t);
          const isEmpty =
            value === null ||
            value === undefined ||
            value === "";

          return (
            <View
              key={`${item.source_step_id}-${item.field}`}
              style={{
                padding: 13,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: c.border,
                backgroundColor: c.input,
              }}
            >
              <Text
                style={{
                  color: c.muted,
                  fontSize: 12,
                  fontWeight: "900",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </Text>

              <Text
                style={{
                  color: isEmpty ? c.muted : c.text,
                  fontSize: 15,
                  lineHeight: 22,
                  fontWeight: isEmpty ? "600" : "800",
                  marginTop: 5,
                }}
              >
                {formatted}
              </Text>
            </View>
          );
        })}
      </View>
    </BlockShell>
  );
}


function LocalJourneyVideoBlock({ block, c, setField, authToken }) {
  const t = useJourneyT();
  const videoUrl = getJourneyAssetUrl(block?.path);
  const posterUrl = getJourneyAssetUrl(block?.poster);
  const [captionTrackUrl, setCaptionTrackUrl] = useState(null);
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [playNotice, setPlayNotice] = useState("");
  const startVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    setPlayNotice("");
    const playing = video.play?.(); // Runs directly in the click handler.
    if (playing?.catch) playing.catch(() => setPlayNotice("Press Play on the video to start."));
  };
  // Local MP4s are authored directly in a Journey, not opened in the Saras
  // full-screen video stage. They need their own authenticated caption fetch.
  useEffect(() => {
    if (Platform.OS !== "web" || !authToken || !block?.path) {
      setCaptionTrackUrl(null);
      return undefined;
    }
    let disposed = false;
    let blobUrl = null;
    const controller = new AbortController();
    setCaptionTrackUrl(null);
    const url = `${FASTAPI_BASE_URL}/wonderlabs/local-video-captions` +
      `?asset_path=${encodeURIComponent(block.path)}&language_id=1`;
    fetch(url, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${authToken}` },
    }).then(async (response) => {
      if (response.status === 204) return null;
      if (!response.ok) throw new Error(`Caption request failed: ${response.status}`);
      return response.text();
    }).then((vtt) => {
      if (disposed || !vtt?.startsWith("WEBVTT")) return;
      blobUrl = URL.createObjectURL(new Blob([vtt], { type: "text/vtt" }));
      setCaptionTrackUrl(blobUrl);
    }).catch((error) => {
      if (!disposed && error?.name !== "AbortError") {
        console.warn("[WonderLabs captions] Local caption unavailable:", error?.message);
      }
    });
    return () => {
      disposed = true;
      controller.abort();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [authToken, block?.path]);

  const markPlayed = () => {
    if (block?.save_as) {
      setField?.(block.save_as, true);
    }
  };

  return (
    <BlockShell
      block={block}
      c={c}
      title={block.title}
      prompt={block.mission}
    >
      {Platform.OS === "web" && videoUrl ? (
        <View>
          {block?.self_guided_active && block?.master_teacher_watch && !started ? (
            <TouchableOpacity accessibilityRole="button" onPress={startVideo}
              style={{ padding: 18, borderRadius: 13, marginBottom: 12,
                alignItems: "center", backgroundColor: c.accent }}>
              <Text style={{ fontSize: 20, fontWeight: "900", color: "#fff", textAlign: "center" }}>
                {`▶ ${block.master_teacher_watch.button || "Watch the clue"}`}
              </Text>
              <Text style={{ fontSize: 15, color: "#fff", marginTop: 6, textAlign: "center" }}>
                {block.master_teacher_watch.why || "Watch for clues before you continue."}
              </Text>
            </TouchableOpacity>
          ) : null}
          {block?.self_guided_active && block?.master_teacher_watch && finished ? (
            <Text style={{ fontSize: 16, fontWeight: "800", color: c.text, marginBottom: 10 }}>
              {block.master_teacher_watch.after || "What clue did you notice? Now try the next part."}
            </Text>
          ) : null}
          {playNotice ? <Text accessibilityRole="alert" style={{ color: c.muted, marginBottom: 8 }}>{playNotice}</Text> : null}
          <video
            ref={videoRef}
            onPlay={() => setStarted(true)}
            aria-label={block?.title || t("Journey science video")}
            controls
            playsInline
            poster={posterUrl || undefined}
            preload="metadata"
            src={videoUrl}
            onEnded={() => { markPlayed(); setFinished(true); }}
            style={{
              backgroundColor: "#000000",
              borderRadius: 12,
              display: "block",
              height: "auto",
              maxHeight: 560,
              width: "100%",
            }}
          >
            {captionTrackUrl && (
              <track kind="captions" src={captionTrackUrl}
                srcLang="en" label="English" default />
            )}
          </video>
          {!!block?.caption && (
            <Text
              style={{
                color: c.muted,
                fontSize: 13,
                lineHeight: 19,
                marginTop: 10,
              }}
            >
              {block.caption}
            </Text>
          )}
        </View>
      ) : (
        <View
          style={{
            alignItems: "center",
            backgroundColor: "#111827",
            borderRadius: 12,
            justifyContent: "center",
            minHeight: 180,
            padding: 24,
          }}
        >
          <Text
            style={{
              color: "#ffffff",
              fontSize: 15,
              lineHeight: 22,
              textAlign: "center",
            }}
          >
            {t("This Journey science video is currently available in the web app.")}
          </Text>
        </View>
      )}
    </BlockShell>
  );
}

function AssetBlock({
  block,
  c,
  onOpenVideo,
  onOpenPwo,
  setField,
  t,
}) {
  // This single new test link is hidden for all regular/teacher-led learners
  // unless deliberately enabled. Native 1168/installation remains untouched.
  const pilotEnabled = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("ff_threejs_pwo") === "1" &&
    new URLSearchParams(window.location.search).get("ff_swan_unified_panel") === "1";
  if (block?.id === "doodle-robot-threejs-pilot" && !pilotEnabled) return null;
  const isVideo = block.type === "video_asset";
  const activity = buildActivityFromBlock(block);

  const openAsset = () => {
    if (block.save_as) {
      setField?.(block.save_as, true);
    }

    if (isVideo) {
      onOpenVideo?.(activity);
    } else {
      onOpenPwo?.(activity);
    }
  };

  return (
    <BlockShell
      block={block}
      c={c}
      title={block.title}
      prompt={block.mission}
    >
      {isVideo && block.self_guided_active && block?.master_teacher_watch?.why ? (
        <Text style={{ color: c.text, fontSize: 16, fontWeight: "800",
          lineHeight: 24, marginBottom: 12 }}>{block.master_teacher_watch.why}</Text>
      ) : null}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={openAsset}
        activeOpacity={0.84}
        style={{
          paddingVertical: 15,
          paddingHorizontal: 16,
          borderRadius: 12,
          backgroundColor: c.accent,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
        }}
      >
        <Text style={{ fontSize: 20 }}>
          {isVideo ? "🎬" : "🎮"}
        </Text>
        <Text
          style={{
            color: "#ffffff",
            fontSize: 15,
            fontWeight: "900",
          }}
        >
          {isVideo ? t(block.self_guided_active ? (block?.master_teacher_watch?.button || "Open Video") : "Open Video")
            : t("Open 3D Exploration")}
        </Text>
      </TouchableOpacity>
    </BlockShell>
  );
}


// Master Teacher pilot: a saved one-check-at-a-time diagnostic conversation.
// Never marks a physical check as verified, and never completes a phase gate.
function MasterTeacherDiagnosticBlock({ block, c, value, setField, contextPayload, onAsk, readOnly }) {
  if (!block.self_guided_active) return null; // Do not add UI to teacher-led mode.
  const state = value && typeof value === "object" ? value : {};
  const cases = Array.isArray(block.cases) ? block.cases : [];
  const chosen = cases.find((entry) => entry.id === state.issue);
  const history = Array.isArray(state.checks) ? state.checks : [];
  const index = Math.min(history.length, (chosen?.checks || []).length);
  const last = history[history.length - 1] || null;
  // Do not rush to a new physical intervention after an unsafe/uncertain clue.
  // Swan gets the observation first; the child deliberately resumes if safe.
  const needsDecision = last && last.result !== "It looks okay" &&
    !state.continue_after_issue;
  const nextCheck = needsDecision ? null : (chosen?.checks?.[index] || null);
  const askSwan = (question, next) => onAsk?.(question, {
    ...contextPayload,
    active_activity: { type: "master_teacher_diagnostic", block_id: block.id,
      issue: chosen?.label || next.issue, current_check: nextCheck?.instruction },
    activity_progress: { ...(contextPayload?.activity_progress || {}),
      diagnostic_state: { ...next, kit: block.kit, issue_label: chosen?.label ||
        cases.find((item) => item.id === next.issue)?.label, checks: next.checks || [] } },
    student_work: { ...(contextPayload?.student_work || {}), [block.save_as]: next },
  });
  const begin = (item) => {
    const next = { issue: item.id, checks: [] };
    setField(block.save_as, next);
    onAsk?.(`My real ${block.kit} has this problem: ${item.label}. ${block.safety} Help me investigate ONE safe check, then wait for what I observe.`, {
      ...contextPayload,
      active_activity: { type: "master_teacher_diagnostic", block_id: block.id },
      activity_progress: { ...(contextPayload?.activity_progress || {}),
        diagnostic_state: { ...next, kit: block.kit, issue_label: item.label } },
      student_work: { ...(contextPayload?.student_work || {}), [block.save_as]: next },
    });
  };
  const report = (result) => {
    if (!nextCheck || readOnly) return;
    const entry = { id: nextCheck.id, check: nextCheck.instruction, result };
    const next = { ...state, continue_after_issue: false, checks: [...history, entry] };
    setField(block.save_as, next);
    askSwan(`I checked: ${nextCheck.instruction}. My report is: ${result}. Earlier checks: ${history.map((x) => `${x.check}: ${x.result}`).join("; ") || "none"}. ${block.safety} What is ONE useful next step? If it is unsafe or needs force, tell me exactly when an adult is required.`, next);
  };
  return <BlockShell block={block} c={c} title={block.title} prompt={block.prompt}>
    <View style={{ gap: 12 }}>
      {!chosen ? <>
        <Text style={{ color: c.text, fontSize: 16, fontWeight: "800" }}>
          What did you notice on your REAL kit? Choose one to investigate with Swan.
        </Text>
        {cases.map((item) => <TouchableOpacity accessibilityRole="button" key={item.id}
          disabled={readOnly} onPress={() => begin(item)}
          style={{ padding: 14, borderRadius: 11, borderWidth: 1, borderColor: c.border,
            backgroundColor: c.input }}>
          <Text style={{ color: c.text, fontSize: 16, fontWeight: "800" }}>{item.label}</Text>
        </TouchableOpacity>)}
      </> : <>
        <Text style={{ color: c.text, fontSize: 16, fontWeight: "900" }}>
          Investigating: {chosen.label}
        </Text>
        {history.length ? <Text style={{ color: c.muted, fontSize: 14 }}>
          Clues reported: {history.map((x) => `${x.check} — ${x.result}`).join("; ")}
        </Text> : null}
        {needsDecision ? <>
          <Text style={{ color: c.text, fontSize: 16, lineHeight: 25, fontWeight: "800" }}>
            You found an important clue. Tell Swan what happened before trying anything else.
            Keep power OFF; don't force or touch damaged parts.
          </Text>
          <TouchableOpacity accessibilityRole="button" disabled={readOnly}
            onPress={() => setField(block.save_as, { ...state, continue_after_issue: true })}
            style={{ borderColor: c.border, borderWidth: 1, borderRadius: 10, padding: 12 }}>
            <Text style={{ color: c.accent, fontWeight: "800" }}>
              Swan and I decided it's safe to check another clue
            </Text>
          </TouchableOpacity>
        </> : nextCheck ? <>
          <Text style={{ color: c.text, fontSize: 18, lineHeight: 26, fontWeight: "900" }}>
            Next check: {nextCheck.instruction}
          </Text>
          <Text style={{ color: c.muted, fontSize: 14 }}>{block.safety}</Text>
          {["It looks okay", "I found something wrong", "I am not sure or cannot check safely"].map((result) =>
            <TouchableOpacity key={result} accessibilityRole="button" disabled={readOnly}
              onPress={() => report(result)} style={{ padding: 13, borderRadius: 11,
                borderWidth: 1, borderColor: c.border, backgroundColor: c.input }}>
              <Text style={{ color: c.text, fontWeight: "800", fontSize: 15 }}>{result}</Text>
            </TouchableOpacity>)}
        </> : <Text style={{ color: c.text, fontSize: 16, lineHeight: 24 }}>
          You've reported all available safe checks. Ask Swan to compare the clues before any new test.
        </Text>}
        {last ? <Text style={{ color: c.muted, fontSize: 13 }}>
          These are your reports, not proof that Swan saw the robot.
        </Text> : null}
        <TouchableOpacity accessibilityRole="button" disabled={readOnly} onPress={() =>
          setField(block.save_as, null)}>
          <Text style={{ color: c.accent, fontWeight: "800" }}>Investigate a different problem</Text>
        </TouchableOpacity>
      </>}
    </View>
  </BlockShell>;
}

function SarasCheckpointBlock({
  block,
  c,
  contextPayload,
  onAsk,
}) {
  const selfGuided = Boolean(block?.self_guided_active);

  return (
    <BlockShell
      block={block}
      c={c}
      title={block.title}
      prompt={
        selfGuided
          ? "Recommended: Explore a case question with Professor Swan, or continue when you're ready."
          : "Choose a question, or ask Saras your own question in the companion panel."
      }
      tone="ai"
    >
      <View style={{ gap: 9 }}>
        {(block.suggested_questions || []).map((question) => (
          <TouchableOpacity
            key={question}
            onPress={() => onAsk?.(question, contextPayload)}
            style={{
              paddingVertical: 11,
              paddingHorizontal: 13,
              borderRadius: 11,
              borderWidth: 1,
              borderColor: c.aiBorder,
              backgroundColor: c.input,
            }}
          >
            <Text
              style={{
                color: c.link,
                fontSize: 15,
                lineHeight: 22,
                fontWeight: "700",
              }}
            >
              {question}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </BlockShell>
  );
}

function CompletionGateBlock({
  block,
  c,
  stepState,
  page,
  t,
  compactSelfGuided = false,
}) {
  const rules = Array.isArray(block.rules) ? block.rules : [];
  const completion = getPageCompletionState(page, stepState);
  const completed = completion.completed;
  const allComplete = completion.allComplete;
  const selfGuided = Boolean(block?.self_guided_active);

  const incompleteTitles = completion.items
    .filter((item) => !item.complete)
    .map((item) => item.label);

  // The left Swan rail already gives the next exact action. In the focused
  // child view the gate is a small progress acknowledgement, not a second
  // checklist repeating the same instructions. Teacher-led copy is unchanged.
  if (compactSelfGuided && selfGuided) {
    return (
      <BlockShell
        block={block}
        c={c}
        title={t(allComplete ? "Required work complete" : "Your progress is saved")}
        prompt={t(allComplete
          ? "You've finished the required activities for this phase."
          : "Keep investigating. Professor Swan shows what to do next.")}
      >
        <Text style={{ color: allComplete ? c.success : c.muted, fontSize: 13, fontWeight: "800" }}>
          {completed} / {rules.length} {t("required activities recorded")}
        </Text>
      </BlockShell>
    );
  }

  return (
    <BlockShell
      block={block}
      c={c}
      title={
        allComplete
          ? t(block.complete_title || "Mission briefing complete")
          : t(block.incomplete_title || "Finish the mission briefing")
      }
      prompt={
        allComplete
          ? t(
              block.complete_prompt ||
                "You have enough evidence and a clear engineering goal for the next investigation."
            )
          : t(
              block.incomplete_prompt ||
                "Your work is saved. Complete the remaining activities before moving on."
            )
      }
    >
      <View
        style={{
          height: 10,
          borderRadius: 999,
          backgroundColor: c.panel,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            height: "100%",
            width: `${
              rules.length ? Math.round((completed / rules.length) * 100) : 0
            }%`,
            backgroundColor: allComplete ? c.success : c.accent,
          }}
        />
      </View>

      {selfGuided ? (
        <View style={{ marginTop: 10, gap: 7 }}>
          <Text
            style={{
              color: allComplete ? c.success : c.text,
              fontSize: 14,
              lineHeight: 20,
              fontWeight: "900",
            }}
          >
            {allComplete
              ? "You finished everything Professor Swan needs for this part."
              : "Here is what to finish before you continue:"}
          </Text>

          {completion.items.map((item) => (
            <Text
              key={item.field}
              style={{
                color: item.complete ? c.success : c.text,
                fontSize: 14,
                lineHeight: 20,
                fontWeight: "700",
              }}
            >
              {item.complete ? "✓" : "○"} {item.label}
            </Text>
          ))}
        </View>
      ) : (
        <>
          <Text
            style={{
              color: allComplete ? c.success : c.muted,
              fontSize: 13,
              fontWeight: "800",
              marginTop: 9,
            }}
          >
            {completed} / {rules.length} required activities complete
          </Text>

          {incompleteTitles.length > 0 ? (
            <View style={{ marginTop: 7, gap: 3 }}>
              <Text
                style={{
                  color: c.muted,
                  fontSize: 12,
                  fontWeight: "800",
                }}
              >
                Still needed:
              </Text>
              {incompleteTitles.map((title) => (
                <Text
                  key={title}
                  style={{
                    color: c.muted,
                    fontSize: 12,
                    fontWeight: "700",
                  }}
                >
                  {"•"} {title}
                </Text>
              ))}
            </View>
          ) : null}
        </>
      )}
    </BlockShell>
  );
}

export default function JourneyPageRenderer({
  page,
  journey,
  step,
  sessionState,
  activityCursorRef,
  onActivityCursorChange,
  onSessionStateChange,
  readOnly = false,
  authToken = null,
  onOpenVideo,
  onOpenPwo,
  onAsk,
  onOpenHelp,
  onContextChange,
  onCompletionChange,
  onCoachChange,
  onActivityNavigationChange,
  onReadAloud,
  isDarkMode = false,
  compactSelfGuidedLayout = false,
  compactLearningViewport = false,
  onRequestEvidenceRecovery,
  onReturnFromEvidenceRecovery,
  evidenceRecoveryReturnTo,
  t = (value) => value,
}) {
  const stepId =
    page?.step_id ||
    step?.step_id ||
    step?.id ||
    "journey-step";

  const stepState = getStepState(sessionState, stepId);
  const activityFirstEnabled = Boolean(
    isWonderLabsActivityFirstEnabled() &&
    page?.blocks?.some((block) => block?.self_guided_active)
  );
  const fairTestPilot = activityFirstEnabled && stepId === "fair-rescue-test";
  const wholeMissionPilot = isWonderLabsActivityFirstEnabled() &&
    Boolean(WONDERLABS_FLOW[stepId]) && Boolean(page?.blocks?.some((b) => b?.self_guided_active));
  const evidenceRecoveryPilot = wholeMissionPilot && Boolean(evidenceRecoveryReturnTo) &&
    evidenceRecoveryReturnTo !== stepId;
  const fairTestStatus = getFloodFairTestStatus(sessionState);
  // Start with evidence, even on a returning visit. Only the child advances;
  // completing a checklist must never unexpectedly swap the whole screen.
  const [selectedFairActivity, setSelectedFairActivity] = useState(0);
  useEffect(() => setSelectedFairActivity(0), [stepId]);
  const [activityMapOpen, setActivityMapOpen] = useState(false);
  const [selectedWholeActivity, setSelectedWholeActivity] = useState(
    () => Number(activityCursorRef?.current?.[stepId] || 0));
  useEffect(() => {
    setSelectedWholeActivity(Number(activityCursorRef?.current?.[stepId] || 0));
  }, [stepId, activityCursorRef]);
  const reportActivityCursor = (intent = "next") => {
    if (!activityCursorRef?.current || !onActivityCursorChange || !wholeMissionPilot) return;
    onActivityCursorChange({
      ...activityCursorRef.current,
      r2BeatCursors: { ...(activityCursorRef.current.r2BeatCursors || {}) },
    }, { intent, stepId });
  };
  const selectWholeActivity = (nextIndex, intent = "review") => {
    const next = Math.max(0, Number(nextIndex) || 0);
    if (activityCursorRef?.current) {
      activityCursorRef.current[stepId] = next;
      // Selecting an activity or advancing into a new one starts at Part 1.
      // Going Back retains the old previous-part behavior.
      if (intent !== "back") startActivityFromBeginning(
        activityCursorRef.current, stepId, missionFlow.activities[next]?.id);
    }
    reportActivityCursor(intent === "back" ? "review" : intent);
    setSelectedWholeActivity(next);
    setShowMissingRequirements(false);
    setOptionalChallengePage(0);
  };
  const [showMissingRequirements, setShowMissingRequirements] = useState(false);
  const [showOptionalChallenge, setShowOptionalChallenge] = useState(false);
  const [optionalChallengePage, setOptionalChallengePage] = useState(0);
  useEffect(() => {
    setShowOptionalChallenge(false);
    setOptionalChallengePage(0);
  }, [stepId]);
  const currentFairIndex = Math.min(
    selectedFairActivity,
    fairTestStatus.maxUnlockedIndex
  );
  const currentFairActivity = fairTestPilot
    ? FLOOD_FAIR_TEST_ACTIVITIES[currentFairIndex]
    : null;

  const authoredCompletionState = useMemo(
    () => getPageCompletionState(page, stepState),
    [page, stepState]
  );
  // The original completion gate only checks fields saved inside Phase 6.
  // V2 additionally requires real evidence from the earlier phases before
  // the footer can advance. This is derived state; it changes no saved data.
  const completionState = useMemo(() => {
    if (!fairTestPilot) return authoredCompletionState;
    const prerequisites = [
      { field: "bridge1_record", label: "Record your Bridge 1 test in Explore",
        targetBlockId: "trial1-summary", complete: fairTestStatus.bridge1Recorded },
      { field: "bridge2_design_change", label: "Finish your Bridge 2 plan in Apply",
        targetBlockId: "trial1-summary", complete: fairTestStatus.bridge2Planned },
    ];
    const allItems = [...prerequisites, ...authoredCompletionState.items];
    return {
      ...authoredCompletionState,
      items: allItems,
      completed: allItems.filter((item) => item.complete).length,
      total: allItems.length,
      allComplete: allItems.length > 0 && allItems.every((item) => item.complete),
      firstIncomplete: allItems.find((item) => !item.complete) || null,
    };
  }, [authoredCompletionState, fairTestPilot,
    fairTestStatus.bridge1Recorded, fairTestStatus.bridge2Planned]);

  const missionFlow = getWonderLabsActivityState(stepId, sessionState, completionState);
  const activeWholeIndex = Math.min(selectedWholeActivity, missionFlow.maxUnlockedIndex);
  const activeWholeActivity = wholeMissionPilot ? missionFlow.activities[activeWholeIndex] : null;
  // R2 is an explicitly selected local experiment. The authored completion,
  // answer persistence, prerequisites and R1 rendering stay authoritative.
  const learningBeatsEnabled = wholeMissionPilot && isWonderLabsLearningBeatsEnabled();
  const learningBeats = useMemo(() => learningBeatsEnabled && activeWholeActivity
      ? resolveLearningBeats(stepId, activeWholeActivity) : [],
    [learningBeatsEnabled, stepId, activeWholeActivity?.id]);
  const currentBeatKey = `${stepId}:${activeWholeActivity?.id || ""}`;
  const [learningBeatSelection, setLearningBeatSelection] = useState({ key: "", index: 0 });
  const rememberedBeat = activityCursorRef?.current?.r2BeatCursors?.[currentBeatKey] ?? 0;
  const selectedBeatIndex = beatProgress(learningBeats,
    learningBeatSelection.key === currentBeatKey ? learningBeatSelection.index : rememberedBeat).index;
  const currentLearningBeat = learningBeatsEnabled ? learningBeats[selectedBeatIndex] : null;
  // Audio and visible media cues are presentation-only and use the current part.
  // They never advance a video, open a PWO, or modify saved answers.
  const mediaCue = useMemo(() => getMasterTeacherMediaCue(
    stepId, page?.blocks, currentLearningBeat),
    [stepId, page?.blocks, currentLearningBeat]);
  const beatPending = Boolean(currentLearningBeat && selectedBeatIndex < learningBeats.length - 1);
  const nextLearningBeat = beatPending ? learningBeats[selectedBeatIndex + 1] : null;
  const beatRequirement = learningBeatRequirement(currentLearningBeat, completionState);
  const canContinueBeat = beatCanAdvance(currentLearningBeat, completionState) &&
    !activeWholeActivity?.missing?.length;
  const selectLearningBeat = (index) => {
    const target = beatProgress(learningBeats, index).index;
    if (activityCursorRef?.current) {
      if (!activityCursorRef.current.r2BeatCursors) activityCursorRef.current.r2BeatCursors = {};
      activityCursorRef.current.r2BeatCursors[currentBeatKey] = target;
    }
    reportActivityCursor(target < selectedBeatIndex ? "review" : "next");
    setLearningBeatSelection({ key: currentBeatKey, index: target });
    setShowMissingRequirements(false);
  };
  const wholeAtEnd = activeWholeIndex >= missionFlow.activities.length - 1;
  const wholeCanAdvance = Boolean(activeWholeActivity?.complete) && !beatPending;
  const goToNextWholeActivity = () => {
    // The footer owns the click; never compute a second target from a closure
    // that was created before the previous React state commit.
    const liveIndex = Number(activityCursorRef?.current?.[stepId] ?? activeWholeIndex);
    if (liveIndex !== activeWholeIndex) return;
    if (wholeCanAdvance && !wholeAtEnd) selectWholeActivity(activeWholeIndex + 1, "next");
  };
  const goToPreviousWholeActivity = () => {
    if (activeWholeIndex > 0) selectWholeActivity(activeWholeIndex - 1, "back");
  };
  // The presentation footer registers its actions with this same state. No
  // extra Next/Back controls compete inside the activity itself.
  useEffect(() => {
    if (!wholeMissionPilot) { onActivityNavigationChange?.(null); return; }
    onActivityNavigationChange?.({
      stepId, index: activeWholeIndex, total: missionFlow.activities.length,
      title: activeWholeActivity?.title || "",
      beatPending, beatIndex: selectedBeatIndex, beatCount: learningBeats.length,
      nextLabel: beatPending ? "Next part" : "Next activity",
      nextTitle: nextLearningBeat?.title || "",
      previousLabel: selectedBeatIndex > 0 ? "Previous part" : "Previous activity",
      missingLabel: beatPending && !canContinueBeat
        ? beatRequirement?.label || "Finish this part before continuing"
        : activeWholeActivity?.missingLabel || activeWholeActivity?.missing?.[0]?.title || "",
      revealMissing: () => setShowMissingRequirements(true),
      canAdvance: beatPending ? canContinueBeat : wholeCanAdvance,
      advance: beatPending ? () => selectLearningBeat(selectedBeatIndex + 1) : goToNextWholeActivity,
      retreat: selectedBeatIndex > 0 ? () => selectLearningBeat(selectedBeatIndex - 1)
        : goToPreviousWholeActivity,
    });
  }, [wholeMissionPilot, stepId, activeWholeIndex, missionFlow.activities.length,
    activeWholeActivity?.title, activeWholeActivity?.missingLabel,
    wholeCanAdvance, beatPending, canContinueBeat, beatRequirement?.label, selectedBeatIndex,
    learningBeats.length, nextLearningBeat?.title, onActivityNavigationChange]);

  const visibleBlocks = useMemo(
    () => getSelfGuidedVisibleBlocks(page, stepState),
    [page, stepState]
  );

  // Presentation-only disclosure state. This is not progress, is never saved,
  // and cannot change authored completion logic. The active group always opens.
  const [reviewGroups, setReviewGroups] = useState({});
  const focusLayout = useMemo(
    () => compactSelfGuidedLayout && !wholeMissionPilot
      ? getJourneyFocusGroups(page, completionState, visibleBlocks)
      : null,
    [compactSelfGuidedLayout, wholeMissionPilot, page, completionState, visibleBlocks]
  );
  const toggleReviewGroup = (groupId) => {
    const key = `${stepId}:${groupId}`;
    setReviewGroups((current) => ({ ...current, [key]: !current[key] }));
  };

  const swanActivity = useMemo(
    () => buildSwanActivityContext(page, stepState, completionState, visibleBlocks),
    [page, stepState, completionState, visibleBlocks]
  );

  const coachState = useMemo(() => {
    if (!page?.self_guided_active && !page?.self_guided) return null;

    const blocks = Array.isArray(page?.blocks) ? page.blocks : [];
    const firstIncomplete = completionState?.firstIncomplete || null;
    const requiredBlock = firstIncomplete?.targetBlockId
      ? blocks.find((block) => block?.id === firstIncomplete.targetBlockId)
      : null;

    // Completion gates answer "may the learner leave this phase?".
    // Coaching answers a different question: "what should the learner do next?".
    // A video can therefore be pedagogically next even when watching it is not
    // itself a completion-gating rule.
    const pendingVideoBlock = (visibleBlocks || []).find((block) => {
      if (block?.type !== "local_video") return false;
      if (!block?.save_as) return false;
      return !Boolean(stepState?.[block.save_as]);
    });

    const requiredIndex = requiredBlock
      ? blocks.findIndex((block) => block?.id === requiredBlock?.id)
      : -1;
    const videoIndex = pendingVideoBlock
      ? blocks.findIndex((block) => block?.id === pendingVideoBlock?.id)
      : -1;

    const targetBlock =
      pendingVideoBlock &&
      (requiredIndex < 0 || (videoIndex >= 0 && videoIndex < requiredIndex))
        ? pendingVideoBlock
        : requiredBlock;

    const guidance = targetBlock?.self_guided_guidance || null;

    const hasStartedRequiredWork =
      Array.isArray(completionState?.items) &&
      completionState.items.some((item) => item.complete);

    const isMediaCue = targetBlock?.type === "local_video";
    const pageGuide = page?.self_guided || {};

    const action = String(
      swanActivity?.next_action?.instruction ||
      (isMediaCue &&
        (guidance?.try_now ||
          targetBlock?.mission ||
          targetBlock?.title)) ||
      (hasStartedRequiredWork &&
        (guidance?.try_now ||
          targetBlock?.self_guided_cue ||
          targetBlock?.prompt)) ||
        pageGuide?.do_now ||
        guidance?.try_now ||
        targetBlock?.prompt ||
        targetBlock?.mission ||
        ""
    ).trim();

    const title = String(
      (isMediaCue &&
        (guidance?.title ||
          targetBlock?.title ||
          "Watch this first")) ||
      (hasStartedRequiredWork &&
        (guidance?.title || targetBlock?.title)) ||
        pageGuide?.title ||
        guidance?.title ||
        targetBlock?.title ||
        "Keep going"
    ).trim();

    return {
      stepId,
      phase: page?.phase || null,
      title: wholeMissionPilot && activeWholeActivity ? activeWholeActivity.title :
        fairTestPilot && currentFairActivity ? currentFairActivity.title : title,
      action: wholeMissionPilot && activeWholeActivity ?
        (activeWholeActivity.missing?.length
          ? `Before continuing, find ${activeWholeActivity.missing[0].title} using the recovery button in this activity.`
          : `${activeWholeActivity.purpose} ${activeWholeActivity.complete
              ? "When you are ready, choose Next activity in the bottom bar."
              : "Use the activity's evidence and controls, or ask me for a hint."}`)
        : fairTestPilot && currentFairActivity ? currentFairActivity.instruction : action,
      complete: Boolean(completionState?.allComplete) && (!wholeMissionPilot || wholeAtEnd),
      firstIncomplete,
      welcome: String(pageGuide?.welcome || "").trim(),
      intro: String(pageGuide?.say || "").trim(),
      transitionSummary: String(
        pageGuide?.transition_summary || ""
      ).trim(),
      transitionEvidence: completionState?.allComplete
        ? buildGroundedPhaseRecap(page, stepState)
        : "",
      nextPreview: String(pageGuide?.next_preview || "").trim(),
    };
  }, [completionState, page, stepId, stepState, visibleBlocks, swanActivity,
      fairTestPilot, currentFairActivity, wholeMissionPilot, activeWholeActivity, wholeAtEnd]);

  useEffect(() => {
    onCompletionChange?.(completionState);
  }, [completionState, onCompletionChange]);

  useEffect(() => {
    onCoachChange?.(coachState);
  }, [coachState, onCoachChange]);

  const setField = (field, nextValue) => {
    if (readOnly) return;
    onSessionStateChange?.((current) => {
      const safeCurrent =
        current && typeof current === "object" ? current : {};

      const safeSteps =
        safeCurrent.steps && typeof safeCurrent.steps === "object"
          ? safeCurrent.steps
          : {};

      const currentStep =
        safeSteps[stepId] && typeof safeSteps[stepId] === "object"
          ? safeSteps[stepId]
          : {};

      return {
        ...safeCurrent,
        steps: {
          ...safeSteps,
          [stepId]: {
            ...currentStep,
            [field]: resolveValue(currentStep[field], nextValue),
          },
        },
      };
    });
  };

  const checkpoint = useMemo(
    () =>
      (page?.blocks || []).find(
        (block) => block.type === "saras_checkpoint"
      ) || null,
    [page]
  );

  const priorWorkForAi = useMemo(() => {
    const allSteps =
      sessionState && typeof sessionState.steps === "object"
        ? sessionState.steps
        : {};

    const summaries = (page?.blocks || []).filter(
      (block) => block.type === "prior_work_summary"
    );

    const result = {};

    summaries.forEach((summary) => {
      (summary.items || []).forEach((item) => {
        const sourceStep =
          allSteps[item.source_step_id] &&
          typeof allSteps[item.source_step_id] === "object"
            ? allSteps[item.source_step_id]
            : {};

        const key =
          item.id ||
          `${item.source_step_id}.${item.field}`;

        result[key] = {
          label: item.label || key,
          source_step_id: item.source_step_id,
          field: item.field,
          value: readNestedValue(sourceStep, item.field),
        };
      });
    });

    return result;
  }, [page, sessionState?.steps]);

  const contextPayload = useMemo(
    () => ({
      journey_id: journey?.journey_id ?? journey?.id ?? null,
      journey_slug:
        journey?.slug ??
        journey?.manifest?.slug ??
        null,
      step_id: stepId,
      step_title:
        page?.title ||
        step?.title ||
        step?.name ||
        null,
      hidden_context: checkpoint?.hidden_context || null,
      // The self-guided flag is on authored blocks, NOT on the page itself in
      // JourneyPanel's current develop implementation. Check the real flags;
      // otherwise Swan's action context and screenshot path silently turn off.
      self_guided_active: Boolean(page?.blocks?.some((block) => block.self_guided_active)),
      // Same completion gates and live recorded fields the renderer uses.
      // This is a snapshot, not a second source of progress truth.
      activity_progress: page?.blocks?.some((block) => block.self_guided_active)
        ? (wholeMissionPilot && activeWholeActivity ? {
              ...swanActivity,
              physical_assembly_report: stepId === "fdr-apply" ? (() => {
                const checklist = page?.blocks?.find((block) => block.id === "fdr-build-check");
                const reports = stepState?.[checklist?.save_as] || {};
                const items = (checklist?.items || []).map((item) => ({
                  item_id: item.id,
                  label: item.label,
                  child_report: ["complete", "help", "not_yet"].includes(reports[item.id])
                    ? reports[item.id] : "unanswered",
                }));
                return {
                  source: "child_self_report_not_independently_verified",
                  checklist_items: items,
                  real_motion_test_recorded: Boolean(stepState?.motion_test),
                  instruction: "Do not infer that the robot is assembled, working, or ready to power on just from visiting the PWO or selecting checklist responses.",
                };
              })() : null,
              media_briefing: mediaCue,
              kit_troubleshooting: getMasterTeacherKitGuide(stepId),
              physical_briefing: currentLearningBeat?.blockIds?.map((id) => {
                const activeBlock = page?.blocks?.find((b) => b.id === id);
                return activeBlock ? getMasterTeacherPhysicalBriefing(stepId, activeBlock) : null;
              }).find(Boolean) || null,
              diagnostic_state: page?.blocks?.filter((b) => b.type === "master_teacher_diagnostic")
                .map((b) => ({ kit: b.kit, ...stepState?.[b.save_as] }))
                .find((v) => v.issue) || null,
              phase_complete: Boolean(completionState?.allComplete) && wholeAtEnd,
              next_action: {
                ...swanActivity?.next_action,
                block_id: (learningBeatsEnabled && currentLearningBeat
                  ? currentLearningBeat.blockIds : activeWholeActivity.blockIds).find((id) =>
                    completionState?.items?.some((item) => item.targetBlockId === id && !item.complete)) ||
                  (learningBeatsEnabled && currentLearningBeat
                    ? currentLearningBeat.blockIds[0] : activeWholeActivity.blockIds[0]) || null,
                instruction: activeWholeActivity.missing.length > 0
                  ? `Find your ${activeWholeActivity.missing[0].title} before continuing. Use the recovery link on this screen.`
                  : mediaCue?.spoken
                    ? `${mediaCue.spoken} This is the intended media action for this part; do not claim the child already watched or opened it.`
                  : learningBeatsEnabled && currentLearningBeat
                    ? `Focus on the current part: ${currentLearningBeat.title}. ${activeWholeActivity.purpose} Help the learner find evidence for this part, without supplying answers or jumping ahead to later parts.`
                    : `${activeWholeActivity.purpose} ${activeWholeActivity.complete ? "Select Next activity when ready." : "Do the investigation and record your own evidence."}`,
              },
              focused_activity: {
                id: activeWholeActivity.id,
                title: activeWholeActivity.title,
                number: activeWholeIndex + 1,
                total: missionFlow.activities.length,
                phase: page?.phase,
                complete: activeWholeActivity.complete,
                missing_prerequisites: activeWholeActivity.missing.map((item) => item.title),
                ...(learningBeatsEnabled && currentLearningBeat ? {
                  learning_beat: {
                    title: currentLearningBeat.title, kind: currentLearningBeat.kind,
                    number: selectedBeatIndex + 1, total: learningBeats.length,
                  },
                } : {}),
              },
            } : fairTestPilot && currentFairActivity
          ? {
              ...swanActivity,
              next_action: {
                ...swanActivity?.next_action,
                instruction: currentFairActivity.instruction,
                block_id: ["trial1-summary", "fair-test-video", "bridge2-record", "comparison-claim"][currentFairIndex],
              },
              focused_activity: {
                id: currentFairActivity.id,
                title: currentFairActivity.title,
                number: currentFairIndex + 1,
                total: FLOOD_FAIR_TEST_ACTIVITIES.length,
                missing_bridge1: !fairTestStatus.bridge1Recorded,
                missing_bridge2_plan: !fairTestStatus.bridge2Planned,
              },
            }
          : swanActivity) : null,
      student_work: {
        ...stepState,
        prior_work: priorWorkForAi,
        prior_theory: sessionState?.theory || null,
        prior_observations: Array.isArray(sessionState?.observations)
          ? sessionState.observations.filter((value) =>
              String(value || "").trim()
            )
          : [],
      },
      suggested_questions:
        checkpoint?.suggested_questions || [],
      reveal_policy: checkpoint?.reveal_policy || null,
      active_activity: wholeMissionPilot && activeWholeActivity
        ? { type: "investigation", id: activeWholeActivity.id,
            title: activeWholeActivity.title, instruction: activeWholeActivity.purpose }
        : fairTestPilot && currentFairActivity
          ? { type: "investigation", title: currentFairActivity.title }
          : { type: "question" },
    }),
    [
      checkpoint,
      journey,
      page,
      swanActivity,
      priorWorkForAi,
      sessionState?.observations,
      sessionState?.theory,
      step,
      stepId,
      stepState,
      fairTestPilot,
      wholeMissionPilot,
      activeWholeActivity,
      activeWholeIndex,
      wholeAtEnd,
      missionFlow.activities.length,
      completionState,
      learningBeatsEnabled, currentLearningBeat, mediaCue, selectedBeatIndex, learningBeats.length,
      currentFairActivity,
      currentFairIndex,
      fairTestStatus.bridge1Recorded,
      fairTestStatus.bridge2Planned,
    ]
  );

  useEffect(() => {
    onContextChange?.(contextPayload);
  }, [contextPayload, onContextChange]);

  const c = {
    bg: isDarkMode ? "#0f172a" : "#ffffff",
    panel: isDarkMode ? "#111827" : "#f8fafc",
    card: isDarkMode ? "#111827" : "#ffffff",
    input: isDarkMode ? "#0b1220" : "#ffffff",
    text: isDarkMode ? "#f8fafc" : "#0f172a",
    muted: isDarkMode ? "#94a3b8" : "#64748b",
    placeholder: isDarkMode ? "#64748b" : "#94a3b8",
    border: isDarkMode
      ? "rgba(148,163,184,0.24)"
      : "#dbe4ee",
    link: isDarkMode ? "#60a5fa" : "#2563eb",
    accent: isDarkMode ? "#38bdf8" : "#0369a1",
    accentSoft: isDarkMode
      ? "rgba(56,189,248,0.12)"
      : "#e0f2fe",
    success: isDarkMode ? "#4ade80" : "#15803d",
    successSoft: isDarkMode
      ? "rgba(74,222,128,0.12)"
      : "#f0fdf4",
    mystery: isDarkMode ? "#c4b5fd" : "#6d28d9",
    mysterySoft: isDarkMode
      ? "rgba(139,92,246,0.13)"
      : "#f5f3ff",
    mysteryBorder: isDarkMode
      ? "rgba(167,139,250,0.35)"
      : "#ddd6fe",
    ai: isDarkMode
      ? "rgba(124,58,237,0.10)"
      : "#faf5ff",
    aiBorder: isDarkMode
      ? "rgba(167,139,250,0.30)"
      : "#e9d5ff",
    instructionalModes: {
      I_DO: {
        accent: isDarkMode ? "#ddd6fe" : "#6d28d9",
        soft: isDarkMode
          ? "rgba(139,92,246,0.15)"
          : "#f5f3ff",
        border: isDarkMode
          ? "rgba(196,181,253,0.42)"
          : "#c4b5fd",
      },
      WE_DO: {
        accent: isDarkMode ? "#5eead4" : "#0f766e",
        soft: isDarkMode
          ? "rgba(20,184,166,0.14)"
          : "#f0fdfa",
        border: isDarkMode
          ? "rgba(94,234,212,0.38)"
          : "#99f6e4",
      },
      YOU_DO: {
        accent: isDarkMode ? "#fbbf24" : "#b45309",
        soft: isDarkMode
          ? "rgba(245,158,11,0.14)"
          : "#fffbeb",
        border: isDarkMode
          ? "rgba(251,191,36,0.40)"
          : "#fcd34d",
      },
    },
  };

  // Both the grouped and original views render EXACTLY the same block components.
  // Grouping only controls disclosure; it never changes saved values or gates.
  const renderJourneyBlock = (sourceBlock) => {
        // Presentation-only decorations: never mutate the authored block or saved data.
        const mediaWatch = getMasterTeacherWatchSpec(stepId, sourceBlock);
        const physicalGuide = getMasterTeacherPhysicalBriefing(stepId, sourceBlock);
        const block = mediaWatch || physicalGuide ? {
          ...sourceBlock,
          ...(mediaWatch && !sourceBlock.master_teacher_watch ? { master_teacher_watch: mediaWatch } : {}),
          ...(physicalGuide && !sourceBlock.master_teacher_briefing ? { master_teacher_briefing: physicalGuide } : {}),
        } : sourceBlock;
        const value = block.save_as
          ? stepState?.[block.save_as]
          : undefined;

        switch (block.type) {
          case "story_prelude":
            return <JourneyStoryPrelude key={block.id} block={block} c={c} t={t} authToken={authToken}
              materials={page?.self_guided?.materials} guidedPrelude={learningBeatsEnabled} />;

          case "story_hook":
            return <StoryHookBlock key={block.id} block={block} c={c} onReadAloud={onReadAloud} />;

          case "image_reveal":
            return (
              <ImageRevealBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                readOnly={readOnly}
                t={t}
              />
            );

          case "image_gallery":
            return (
              <ImageGalleryBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                visited={stepState?.[`${block.save_as}_viewed`]}
                setField={setField}
                readOnly={readOnly}
              />
            );

          case "matching":
            return (
              <MatchingBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                readOnly={readOnly}
              />
            );

          case "sequence_builder":
            return (
              <SequenceBuilderBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                readOnly={readOnly}
                t={t}
                r2Shuffle={learningBeatsEnabled}
              />
            );

          case "quiz_question":
            return (
              <QuizQuestionBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                readOnly={readOnly}
              />
            );

          case "choice_cards":
            return (
              <ChoiceCardsBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                readOnly={readOnly}
              />
            );

          case "multi_select":
            return (
              <ChoiceCardsBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                readOnly={readOnly}
                multi
              />
            );

          case "evidence_response":
            return (
              <EvidenceResponseBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                readOnly={readOnly}
                showValidation={showMissingRequirements && completionState?.items?.some(
                  (item) => item.targetBlockId === block.id && !item.complete)}
              />
            );

          case "classification":
            return (
              <ClassificationBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                readOnly={readOnly}
              />
            );

          case "prior_work_summary":
            return (
              <PriorWorkSummaryBlock
                key={block.id}
                block={block}
                c={c}
                sessionState={sessionState}
              />
            );

          case "build_checklist":
            return (
              <BuildChecklistBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                contextPayload={contextPayload}
                onAsk={onAsk}
                onReadAloud={onReadAloud}
                readOnly={readOnly}
              />
            );

          case "experiment_record":
            return (
              <ExperimentRecordBlock
                key={block.id}
                block={block}
                c={c}
                value={value}
                setField={setField}
                onReadAloud={onReadAloud}
                readOnly={readOnly}
              />
            );

          case "local_video":
            return (
              <LocalJourneyVideoBlock
                key={block.id}
                block={block}
                c={c}
                setField={setField}
                authToken={authToken}
              />
            );

          case "video_asset":
          case "pwo_asset":
            return (
              <AssetBlock
                key={block.id}
                block={block}
                c={c}
                onOpenVideo={onOpenVideo}
                onOpenPwo={onOpenPwo}
                setField={setField}
                t={t}
              />
            );

          case "master_teacher_diagnostic":
            return (
              <MasterTeacherDiagnosticBlock
                key={block.id} block={block} c={c} value={value}
                setField={setField} contextPayload={contextPayload}
                onAsk={onAsk} readOnly={readOnly}
              />
            );

          case "saras_checkpoint":
            return (
              <SarasCheckpointBlock
                key={block.id}
                block={block}
                c={c}
                contextPayload={contextPayload}
                onAsk={onAsk}
              />
            );

          case "completion_gate":
            return (
              <CompletionGateBlock
                key={block.id}
                block={block}
                c={c}
                stepState={stepState}
                page={page}
                t={t}
                compactSelfGuided={Boolean(focusLayout)}
              />
            );

          default:
            console.warn(
              "[JourneyPageRenderer] Unsupported block type:",
              block.type
            );
            return null;
        }
  };

  return (
    <JourneyTContext.Provider value={t}>
    <MissionFocusedContext.Provider value={wholeMissionPilot}>
    <View
      style={{
        width: "100%",
        maxWidth: compactSelfGuidedLayout ? (compactLearningViewport ? 1380 : 1120) : 960,
        alignSelf: compactSelfGuidedLayout ? "flex-start" : "center",
      }}
    >
      <Text
        style={{
          color: c.text,
          fontSize: compactLearningViewport ? 22 : 28,
          lineHeight: compactLearningViewport ? 28 : 34,
          fontWeight: "900",
        }}
      >
        {page?.title}
      </Text>

      {!!page?.subtitle && (
        <Text
          style={{
            color: c.accent,
            fontSize: compactLearningViewport ? 14 : 17,
            fontWeight: "800",
            marginTop: compactLearningViewport ? 2 : 5,
          }}
        >
          {page.subtitle}
        </Text>
      )}

      {!!page?.story && (!focusLayout && !fairTestPilot && !wholeMissionPilot ? (
        <Text
          style={{
            color: c.text,
            fontSize: 16,
            lineHeight: 25,
            marginTop: 13,
          }}
        >
          {page.story}
        </Text>
      ) : (
        <View style={{ marginTop: compactLearningViewport ? 2 : 8 }}>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={t("Toggle mission briefing")}
            accessibilityState={{ expanded: Boolean(reviewGroups[`${stepId}:briefing`]) }}
            onPress={() => toggleReviewGroup("briefing")}
            style={{ paddingVertical: 6, alignSelf: "flex-start" }}
          >
            <Text style={{ color: c.accent, fontSize: 13, fontWeight: "800" }}>
              {t(reviewGroups[`${stepId}:briefing`] ? "Hide mission briefing −" : "Review mission briefing +")}
            </Text>
          </TouchableOpacity>
          {reviewGroups[`${stepId}:briefing`] ? (
            <Text style={{ color: c.text, fontSize: 15, lineHeight: 23, marginTop: 4 }}>
              {page.story}
            </Text>
          ) : null}
        </View>
      ))}

      {!focusLayout && !wholeMissionPilot && (page?.blocks || [])
        .filter((block) => block.type === "story_prelude")
        .map((block) => (
          <JourneyStoryPrelude key={block.id} block={block} c={c} t={t} authToken={authToken}
            materials={page?.self_guided?.materials} />
        ))}

      {!fairTestPilot && !wholeMissionPilot && !page?.self_guided_active ? (
        <InstructionalFlowSummary
          flow={page?.instructional_flow}
          c={c}
        />
      ) : null}

      {!focusLayout && !fairTestPilot && !wholeMissionPilot ? <ProfessorSwanHelpPrompt
        checkpoint={checkpoint}
        c={c}
        contextPayload={contextPayload}
        onOpenHelp={onOpenHelp}
      /> : null}

      {evidenceRecoveryPilot ? (
        <View style={{ marginTop: 12, padding: 14, borderRadius: 12, backgroundColor: c.accentSoft,
          borderWidth: 1, borderColor: c.accent }}>
          <Text style={{ fontSize: 16, color: c.text, fontWeight: "900" }}>
            {t("You're recording earlier evidence")}
          </Text>
          <Text style={{ color: c.text, marginTop: 5 }}>
            {t("Use the activities below to record what really happened. Then return to the investigation where you left off.")}
          </Text>
          <TouchableOpacity accessibilityRole="button" onPress={onReturnFromEvidenceRecovery}
            style={{ marginTop: 10, padding: 11, borderWidth: 1, borderColor: c.accent,
              borderRadius: 9, alignSelf: "flex-start" }}>
            <Text style={{ color: c.accent, fontWeight: "800" }}>{t("Return to your investigation →")}</Text>
          </TouchableOpacity>
        </View>
      ) : null}
      {wholeMissionPilot && activeWholeActivity ? (
        <View style={{ marginTop: compactLearningViewport ? 6 : 12 }}>
          <WonderLabsProgressNavigator
            label={t("Activities")}
            currentIndex={activeWholeIndex}
            items={missionFlow.activities.map((activity, idx) => ({
              id: activity.id,
              title: t(activity.title),
              shortLabel: `${t("Activity")} ${idx + 1}`,
              complete: Boolean(activity.complete),
              locked: idx > missionFlow.maxUnlockedIndex,
            }))}
            onSelect={(nextIndex) => selectWholeActivity(nextIndex, "review")}
            expanded={activityMapOpen}
            onExpandedChange={setActivityMapOpen}
            accent={c.accent}
            border={c.border}
            text={c.text}
            muted={c.muted}
            surface={c.card}
            background={c.accentSoft}
          />
          <View style={{ marginTop: compactLearningViewport ? 7 : 12, padding: compactLearningViewport ? 12 : 17, backgroundColor: c.card,
            borderWidth: 1, borderColor: c.accent, borderRadius: 14 }}>
            <Text style={{ color: c.accent, fontSize: 12, fontWeight: "900" }}>
              {t("ACTIVITY")} {activeWholeIndex + 1} {t("OF")} {missionFlow.activities.length}
            </Text>
            {learningBeatsEnabled && learningBeats.length > 1 ? (
              <Text style={{ color: c.muted, fontSize: 14, fontWeight: "700", marginTop: 5 }}>
                {t(activeWholeActivity.title)}
              </Text>
            ) : (
              <>
                <Text style={{ color: c.text, fontSize: compactLearningViewport ? 21 : 24, fontWeight: "900", marginTop: compactLearningViewport ? 3 : 7 }}>
                  {t(activeWholeActivity.title)}
                </Text>
                <Text style={{ color: c.text, fontSize: compactLearningViewport ? 15 : 16, lineHeight: compactLearningViewport ? 21 : 24, marginTop: compactLearningViewport ? 4 : 8 }}>
                  {t(activeWholeActivity.purpose)}
                </Text>
              </>
            )}
            {!learningBeatsEnabled && activeWholeIndex === 0 && String(page?.phase || "").toLowerCase() === "prelude" &&
              Array.isArray(page?.self_guided?.materials) ? (
              <View style={{ marginTop: 13, padding: 12, borderRadius: 10,
                backgroundColor: c.panel }}>
                <Text style={{ color: c.text, fontSize: 17, fontWeight: "900" }}>
                  {t("Get your experiment materials ready")}
                </Text>
                {page.self_guided.materials.map((material, idx) => (
                  <Text key={idx} style={{ color: c.text, fontSize: 14,
                    lineHeight: 22, marginTop: 5 }}>• {t(material)}</Text>
                ))}
                <Text style={{ color: c.muted, fontSize: 13, marginTop: 8 }}>
                  {t("Ask an adult to help make the workspace safe. Don't test your bridge yet.")}
                </Text>
              </View>
            ) : null}
            {((stepId === "flat-bridge-test" && activeWholeIndex === 0) ||
              (stepId === "fair-rescue-test" && [1,2].includes(activeWholeIndex))) ? (
              <View style={{ marginTop: 10, padding: 10,
                backgroundColor: c.mysterySoft, borderRadius: 10,
                borderWidth: 1, borderColor: c.mysteryBorder }}>
                <Text style={{ color: c.text, fontSize: 13, fontWeight: "700" }}>
                  {t("Safety: use stable supports and small non-breakable loads. Keep the area clear, and stop if the setup becomes unstable.")}
                </Text>
              </View>
            ) : null}
            {stepId !== "flat-bridge-test" && stepId !== "fair-rescue-test" &&
              (activeWholeActivity.blockIds || []).some((id) =>
                (page?.blocks || []).some((block) => block.id === id &&
                  (block.type === "build_checklist" || block.type === "experiment_record"))) &&
              (!learningBeatsEnabled || learningBeats.length <= 1 || ["safety", "record"].includes(currentLearningBeat?.kind)) &&
              Array.isArray(page?.self_guided?.safety) && page.self_guided.safety.length > 0 ? (
              <View style={{ marginTop: 10, padding: 11, backgroundColor: c.mysterySoft,
                borderRadius: 10, borderWidth: 1, borderColor: c.mysteryBorder }}>
                <Text style={{ color: c.text, fontSize: 13, fontWeight: "800" }}>
                  {t("Before handling your kit")}
                </Text>
                {(page.self_guided.safety || []).slice(0, 3).map((rule, i) => (
                  <Text key={i} style={{ color: c.text, fontSize: 13, lineHeight: 19, marginTop: 4 }}>
                    {"• "}{t(rule)}
                  </Text>
                ))}
              </View>
            ) : null}
            {stepId !== "flat-bridge-test" && stepId !== "fair-rescue-test" &&
              (activeWholeActivity.blockIds || []).some((id) =>
                (page?.blocks || []).some((block) => block.id === id &&
                  (block.type === "build_checklist" || block.type === "experiment_record"))) &&
              !learningBeatsEnabled &&
              Array.isArray(page?.self_guided?.safety) && page.self_guided.safety.length > 0 ? (
              <View style={{ marginTop: 10, padding: 11, backgroundColor: c.mysterySoft,
                borderRadius: 10, borderWidth: 1, borderColor: c.mysteryBorder }}>
                <Text style={{ color: c.text, fontSize: 13, fontWeight: "800" }}>
                  {t("Before handling your kit")}
                </Text>
                {(page.self_guided.safety || []).slice(0, 3).map((rule, i) => (
                  <Text key={i} style={{ color: c.text, fontSize: 13, lineHeight: 19, marginTop: 4 }}>
                    {"• "}{t(rule)}
                  </Text>
                ))}
              </View>
            ) : null}
            {showMissingRequirements && !activeWholeActivity.complete ? (
              <View style={{ marginTop: 12, padding: 12, borderRadius: 10,
                backgroundColor: "#fff1f2", borderWidth: 1, borderColor: "#e11d48" }}>
                <Text style={{ color: "#9f1239", fontWeight: "900" }}>
                  {t("Before you can continue")}
                </Text>
                <Text style={{ color: "#881337", marginTop: 5 }}>
                  {t(learningBeatsEnabled && beatPending && beatRequirement
                    ? beatRequirement.label
                    : activeWholeActivity.missingLabel || activeWholeActivity.missing?.[0]?.title ||
                      "Complete the required activity above, then try again.")}
                </Text>
              </View>
            ) : null}
            {activeWholeActivity.missing?.map((missing) => (
              <View key={`${missing.stepId}:${missing.field}`}
                style={{ marginTop: 12, padding: 13, backgroundColor: c.panel, borderRadius: 10 }}>
                <Text style={{ color: c.text, fontWeight: "800" }}>
                  {t(`${missing.title} hasn't been recorded yet.`)}
                </Text>
                <Text style={{ marginTop: 4, color: c.muted }}>
                  {t("You'll return to this activity after recording real evidence.")}
                </Text>
                <TouchableOpacity accessibilityRole="button"
                  onPress={() => onRequestEvidenceRecovery?.(missing.stepId)}
                  style={{ marginTop: 9, backgroundColor: c.accent, padding: 10,
                    borderRadius: 8, alignSelf: "flex-start" }}>
                  <Text style={{ color: "#ffffff", fontWeight: "800" }}>
                    {t(`Go record ${missing.title} →`)}
                  </Text>
                </TouchableOpacity>
              </View>
            ))}
            {stepId === "fair-rescue-test" && activeWholeIndex === 0 ? (
              <View style={{ marginTop: 13, gap: 9 }}>
                <View style={{ padding: 12, borderRadius: 10, backgroundColor: c.panel }}>
                  <Text style={{ color: c.text, fontWeight: "900" }}>{t("Bridge 1 — starting result")}</Text>
                  <Text style={{ color: c.text, marginTop: 6, lineHeight: 22 }}>
                    {fairTestStatus.bridge1Recorded
                      ? `${t("Held")}: ${fairTestStatus.bridge1.capacity} ${t("supply units")}; ${t("bent")}: ${fairTestStatus.bridge1.failure_location}. ${fairTestStatus.bridge1.failure_observation}`
                      : t("No real result recorded yet. Use the button above.")}
                  </Text>
                </View>
                <View style={{ padding: 12, borderRadius: 10, backgroundColor: c.panel }}>
                  <Text style={{ color: c.text, fontWeight: "900" }}>{t("Bridge 2 — your planned change")}</Text>
                  <Text style={{ color: c.text, marginTop: 6, lineHeight: 22 }}>
                    {fairTestStatus.bridge2Planned
                      ? `${fairTestStatus.bridge2Plan.bridge2_design_change} — ${fairTestStatus.bridge2Plan.bridge2_design_prediction}`
                      : t("No design plan recorded yet. Use the button above.")}
                  </Text>
                </View>
              </View>
            ) : null}
            {stepId === "next-variable-mission" && activeWholeActivity.id === "next" &&
                stepState?.next_variable_choice ? (
              <View style={{ padding: 12, borderRadius: 9, marginTop: 12,
                backgroundColor: c.accentSoft, borderColor: c.border, borderWidth: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: "800", color: c.text }}>
                  {t("Keep your next test consistent")}
                </Text>
                <Text style={{ marginTop: 5, color: c.text, lineHeight: 21 }}>
                  {t("Check that your written plan investigates the one change you selected, and says what stays the same and what you will measure. You can revise either answer.")}
                </Text>
              </View>
            ) : null}
            {learningBeatsEnabled && learningBeats.length > 1 ? (
              <View accessible accessibilityRole="text"
                accessibilityLabel={`Part ${selectedBeatIndex + 1} of ${learningBeats.length}: ${currentLearningBeat.title}`}
                style={{ marginTop: 8, marginBottom: 6, flexDirection: "row",
                  alignItems: "center", flexWrap: "wrap", gap: 9 }}>
                <Text style={{ color: c.text, fontSize: 16, fontWeight: "900", flexShrink: 1 }}>
                  {t(currentLearningBeat.title)}
                </Text>
                <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                  {learningBeats.map((beat, i) => (
                    <View key={beat.id} style={{ width: i === selectedBeatIndex ? 11 : 8,
                      height: i === selectedBeatIndex ? 11 : 8, borderRadius: 999,
                      backgroundColor: i < selectedBeatIndex ? "#258C75"
                        : i === selectedBeatIndex ? c.accent : c.border }} />
                  ))}
                </View>
                <Text style={{ fontSize: 12, color: c.muted }}>
                  {selectedBeatIndex + 1}/{learningBeats.length}
                </Text>
                {beatRequirement ? (
                  <Text style={{ fontSize: 12, color: c.muted, flexBasis: "100%" }}>
                    {t(`To continue: ${beatRequirement.label}`)}
                  </Text>
                ) : null}
              </View>
            ) : null}
            {mediaCue ? (
              <View style={{ marginTop: 9, marginBottom: 4, padding: 12,
                backgroundColor: c.accentSoft, borderColor: c.border,
                borderWidth: 1, borderRadius: 11, gap: 8 }}>
                <Text style={{ fontSize: 15, fontWeight: "800", color: c.text,
                  lineHeight: 22 }}>
                  {t(mediaCue.short)}
                </Text>
                <TouchableOpacity accessibilityRole="button"
                  accessibilityLabel="Hear Professor Swan explain what to open"
                  onPress={() => onReadAloud?.(mediaCue.spoken)}
                  style={{ alignSelf: "flex-start", paddingVertical: 8,
                    paddingHorizontal: 11, backgroundColor: c.accent,
                    borderRadius: 9 }}>
                  <Text style={{ color: "#fff", fontSize: 14, fontWeight: "900" }}>
                    {t("🔊 Hear Swan's guide")}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : null}
            {(visibleBlocks || []).filter((block) => activeWholeActivity.blockIds.includes(block.id) &&
              (!learningBeatsEnabled || !currentLearningBeat || currentLearningBeat.blockIds.includes(block.id))).map((block) => {
              if (stepId === "fair-rescue-test" && block.id === "trial1-summary") return null;
              if (block.type === "pwo_asset" && wholeMissionPilot) {
                const guided = stepId === "wind-car-explore"
                  ? "In the 3D rover, find the motor, propeller, wheels and wiring. Rotate the model and tap the parts. Does the motor turn the wheels directly?"
                  : stepId === "wind-car-understand"
                    ? "You're returning to the 3D parts tray. This time, tap each part and work out what it does in the complete system."
                    : stepId === "wind-car-apply"
                      ? "Follow the assembly views in order with batteries disconnected. Compare each virtual step with your real build."
                      : `Look for evidence for this activity: ${activeWholeActivity.purpose}`;
                return <View key={block.id} style={{ marginTop: 12 }}>
                  <View style={{ backgroundColor: c.accentSoft, borderRadius: 10, padding: 12,
                    borderColor: c.border, borderWidth: 1 }}>
                    <Text style={{ color: c.accent, fontWeight: "900", fontSize: 12 }}>
                      {t("YOUR 3D EXPLORATION MISSION")}
                    </Text>
                    <Text style={{ color: c.text, marginTop: 6, lineHeight: 21 }}>{t(guided)}</Text>
                    <Text style={{ color: c.muted, fontSize: 12, marginTop: 7 }}>
                      {t("Use the model's close X to return here. Your activity will stay in place.")}
                    </Text>
                  </View>
                  {renderJourneyBlock(block)}
                </View>;
              }
              if (block.type !== "build_checklist") return renderJourneyBlock(block);
              // Keep existing save_as keys and the authored completion gate.
              // A compact checklist does not make an item true until clicked.
              const marked = stepState?.[block.save_as] || {};
              return <View key={block.id} style={{ marginTop: 13, padding: 12,
                borderWidth: 1, borderColor: c.border, borderRadius: 12 }}>
                <Text style={{ fontSize: 17, color: c.text, fontWeight: "900" }}>{t(block.title)}</Text>
                <Text style={{ color: c.muted, fontSize: 14, marginVertical: 8 }}>{t(block.prompt)}</Text>
                {(block.items || []).map((item) => {
                  const checked = marked[item.id] === "complete";
                  return <TouchableOpacity key={item.id} accessibilityRole="checkbox"
                    accessibilityState={{ checked, disabled: readOnly }} disabled={readOnly}
                    onPress={() => setField(block.save_as, (previous) => ({
                      ...(previous && typeof previous === "object" ? previous : {}),
                      [item.id]: checked ? undefined : "complete",
                    }))}
                    style={{ flexDirection: "row", gap: 9, alignItems: "center",
                      paddingVertical: 9, borderBottomWidth: 1, borderColor: c.border }}>
                    <Text style={{ fontSize: 18, color: checked ? c.success : c.muted }}>
                      {checked ? "☑" : "☐"}
                    </Text>
                    <Text style={{ color: c.text, fontSize: 14, flex: 1 }}>{t(item.label || item.text)}</Text>
                  </TouchableOpacity>;
                })}
                <Text style={{ marginTop: 9, fontSize: 12, color: c.muted }}>
                  {t("Check only what you verified on your real experiment—not what you hope is true.")}
                </Text>
              </View>;
            })}
            {Array.isArray(activeWholeActivity.optionalBlockIds) && activeWholeActivity.optionalBlockIds.length > 0 ? (
              <View style={{ marginTop: 13, padding: 12, borderRadius: 11,
                borderWidth: 1, borderColor: c.border, backgroundColor: c.panel }}>
                <TouchableOpacity accessibilityRole="button"
                  accessibilityLabel={t("Toggle optional independent challenge")}
                  accessibilityState={{ expanded: showOptionalChallenge }}
                  onPress={() => setShowOptionalChallenge((value) => !value)}>
                  <Text style={{ color: c.accent, fontSize: 15, fontWeight: "900" }}>
                    {t(activeWholeActivity.optionalTitle || "Optional independent challenge")}{showOptionalChallenge ? " −" : " +"}
                  </Text>
                </TouchableOpacity>
                {showOptionalChallenge ? (() => {
                  const optional = (visibleBlocks || []).filter((block) =>
                    activeWholeActivity.optionalBlockIds.includes(block.id));
                  // Optional enrichment is three small investigations, not a
                  // mandatory six-block scroll. The underlying saved fields
                  // and optional completion policy remain unchanged.
                  const optionalPages = [];
                  for (let i = 0; i < optional.length; i += 2) optionalPages.push(optional.slice(i, i + 2));
                  const at = Math.min(optionalChallengePage, Math.max(0, optionalPages.length - 1));
                  return <View style={{ marginTop: 12 }}>
                    <Text style={{ color: c.muted, fontSize: 13, fontWeight: "800", marginBottom: 9 }}>
                      {t("Independent challenge")} {at + 1} {t("of")} {optionalPages.length}
                      {" · "}{t("You can finish your adventure without completing this challenge.")}
                    </Text>
                    {(optionalPages[at] || []).map(renderJourneyBlock)}
                    <View style={{ flexDirection: "row", gap: 9, marginTop: 12, flexWrap: "wrap" }}>
                      {at > 0 ? <TouchableOpacity accessibilityRole="button"
                        onPress={() => setOptionalChallengePage(at - 1)}
                        style={{ padding: 10, borderWidth: 1, borderColor: c.border, borderRadius: 8 }}>
                        <Text style={{ color: c.text, fontWeight: "800" }}>{t("← Previous challenge part")}</Text>
                      </TouchableOpacity> : null}
                      {at < optionalPages.length - 1 ? <TouchableOpacity accessibilityRole="button"
                        onPress={() => setOptionalChallengePage(at + 1)}
                        style={{ padding: 10, borderWidth: 1, borderColor: c.accent, borderRadius: 8 }}>
                        <Text style={{ color: c.accent, fontWeight: "800" }}>{t("Next challenge part →")}</Text>
                      </TouchableOpacity> : null}
                      <TouchableOpacity accessibilityRole="button"
                        onPress={() => setShowOptionalChallenge(false)}
                        style={{ padding: 10, borderWidth: 1, borderColor: c.border, borderRadius: 8 }}>
                        <Text style={{ color: c.muted, fontWeight: "800" }}>{t("Close optional challenge")}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>;
                })() : null}
              </View>
            ) : null}
            {!learningBeatsEnabled ? (
            <TouchableOpacity accessibilityRole="button"
              accessibilityLabel={t("Ask Professor Swan about this activity")}
              onPress={() => onOpenHelp?.(contextPayload)}
              style={{ marginTop: 14, alignSelf: "flex-start", padding: 10,
                backgroundColor: c.ai, borderWidth: 1, borderColor: c.aiBorder,
                borderRadius: 10 }}>
              <Text style={{ color: c.text, fontWeight: "800" }}>
                {t("🦢 Ask Professor Swan about this activity")}
              </Text>
            </TouchableOpacity>
            ) : null}
            {!activeWholeActivity.complete && activeWholeActivity.missing?.length > 0 ? (
              <Text style={{ color: c.muted, marginTop: 12, fontSize: 13 }}>
                {t("Record your missing real evidence using the recovery link above.")}
              </Text>
            ) : null}
          </View>
        </View>
      ) : fairTestPilot ? (
        <View style={{ marginTop: 14 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {FLOOD_FAIR_TEST_ACTIVITIES.map((activity, index) => {
              const locked = index > fairTestStatus.maxUnlockedIndex;
              const active = index === currentFairIndex;
              return (
                <TouchableOpacity key={activity.id} accessibilityRole="button"
                  accessibilityLabel={`${t("Activity")} ${index + 1}: ${t(activity.title)}${locked ? ". Locked" : ""}`}
                  accessibilityState={{ disabled: locked, selected: active }}
                  disabled={locked}
                  onPress={() => setSelectedFairActivity(index)}
                  style={{ flex: 1, minWidth: 105, paddingVertical: 10, paddingHorizontal: 8,
                    borderRadius: 9, alignItems: "center", borderWidth: 1,
                    borderColor: active ? c.accent : c.border,
                    backgroundColor: active ? c.accentSoft : c.panel }}>
                  <Text style={{ color: locked ? c.muted : c.text, fontWeight: active ? "900" : "700", fontSize: 12 }}>
                    {fairTestStatus.completed[index] ? "✓ " : locked ? "○ " : ""}{index + 1}. {t(["Evidence", "Fair test", "Test Bridge 2", "Explain"][index])}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <View style={{ padding: 17, borderRadius: 14, borderWidth: 1, borderColor: c.accent,
            backgroundColor: c.card, marginTop: 14 }}>
            <Text style={{ color: c.accent, fontSize: 12, fontWeight: "900" }}>
              {t("ACTIVITY")} {currentFairIndex + 1} {t("OF")} {FLOOD_FAIR_TEST_ACTIVITIES.length}
            </Text>
            <Text style={{ color: c.text, fontWeight: "900", fontSize: 24, marginTop: 7 }}>
              {t(currentFairActivity.title)}
            </Text>
            <Text style={{ color: c.text, fontSize: 16, lineHeight: 24, marginTop: 9 }}>
              {t(currentFairActivity.purpose)}
            </Text>
            {currentFairIndex === 0 ? (
              <View style={{ marginTop: 14, gap: 10 }}>
                <View style={{ padding: 13, borderRadius: 10, backgroundColor: c.panel }}>
                  <Text style={{ color: c.text, fontWeight: "900", fontSize: 16 }}>{t("Bridge 1 — starting result")}</Text>
                  <Text style={{ color: c.text, marginTop: 5, lineHeight: 22 }}>
                    {fairTestStatus.bridge1Recorded
                      ? `${t("Held")}: ${fairTestStatus.bridge1.capacity} ${t("supply units")} · ${t("Bent")}: ${fairTestStatus.bridge1.failure_location}. ${fairTestStatus.bridge1.failure_observation}`
                      : t("Not recorded. We need a real Bridge 1 test before comparing designs.")}
                  </Text>
                  {!fairTestStatus.bridge1Recorded ? (
                    <TouchableOpacity accessibilityRole="button" onPress={() => onRequestEvidenceRecovery?.("flat-bridge-test")}
                      style={{ marginTop: 10, padding: 10, borderRadius: 9, backgroundColor: c.accent, alignSelf: "flex-start" }}>
                      <Text style={{ color: "#ffffff", fontWeight: "800" }}>{t("Go record Bridge 1 →")}</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
                <View style={{ padding: 13, borderRadius: 10, backgroundColor: c.panel }}>
                  <Text style={{ color: c.text, fontWeight: "900", fontSize: 16 }}>{t("Bridge 2 — your design decision")}</Text>
                  <Text style={{ color: c.text, marginTop: 5, lineHeight: 22 }}>
                    {fairTestStatus.bridge2Planned
                      ? `${fairTestStatus.bridge2Plan.bridge2_design_change} ${fairTestStatus.bridge2Plan.bridge2_design_prediction}`
                      : t("No complete design plan yet. Decide what you will change and why before running the second test.")}
                  </Text>
                  {!fairTestStatus.bridge2Planned ? (
                    <TouchableOpacity accessibilityRole="button" onPress={() => onRequestEvidenceRecovery?.("engineer-bridge-two")}
                      style={{ marginTop: 10, padding: 10, borderRadius: 9, backgroundColor: c.accent, alignSelf: "flex-start" }}>
                      <Text style={{ color: "#ffffff", fontWeight: "800" }}>{t("Go finish Bridge 2 plan →")}</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>
            ) : null}
            {currentFairIndex === 1 ? (visibleBlocks || []).filter((block) =>
              ["fair-test-video", "fair-test-checklist"].includes(block.id)).map((block) => renderJourneyBlock({ ...block, self_guided_guidance: null,
                instructional_mode: null })) : null}
            {currentFairIndex === 2 ? (visibleBlocks || []).filter((block) =>
              block.id === "bridge2-record").map((block) => renderJourneyBlock({ ...block, self_guided_guidance: null,
                instructional_mode: null })) : null}
            {currentFairIndex === 3 ? (visibleBlocks || []).filter((block) =>
              block.id === "comparison-claim").map((block) => renderJourneyBlock({ ...block, self_guided_guidance: null,
                instructional_mode: null })) : null}
            <TouchableOpacity accessibilityRole="button"
              accessibilityLabel={t("Ask Professor Swan about this activity")}
              onPress={() => onOpenHelp?.(contextPayload)}
              style={{ marginTop: 14, alignSelf: "flex-start", padding: 10, borderRadius: 11,
                borderWidth: 1, borderColor: c.aiBorder, backgroundColor: c.ai }}>
              <Text style={{ color: c.text, fontWeight: "800" }}>{t("🦢 Ask Professor Swan about this activity")}</Text>
            </TouchableOpacity>
            <View style={{ marginTop: 18, flexDirection: "row", gap: 10, alignItems: "center" }}>
              {currentFairIndex > 0 ? (
                <TouchableOpacity accessibilityRole="button" onPress={() => setSelectedFairActivity(currentFairIndex - 1)}
                  style={{ padding: 11, borderWidth: 1, borderColor: c.border, borderRadius: 9 }}>
                  <Text style={{ color: c.text, fontWeight: "800" }}>{t("← Previous activity")}</Text>
                </TouchableOpacity>
              ) : null}
              {currentFairIndex < FLOOD_FAIR_TEST_ACTIVITIES.length - 1 ? (
                <TouchableOpacity accessibilityRole="button"
                  disabled={!fairTestStatus.completed[currentFairIndex]}
                  accessibilityState={{ disabled: !fairTestStatus.completed[currentFairIndex] }}
                  onPress={() => setSelectedFairActivity(currentFairIndex + 1)}
                  style={{ padding: 11, borderRadius: 9,
                    backgroundColor: fairTestStatus.completed[currentFairIndex] ? c.accent : c.border }}>
                  <Text style={{ color: fairTestStatus.completed[currentFairIndex] ? "#ffffff" : c.muted, fontWeight: "900" }}>
                    {t(fairTestStatus.completed[currentFairIndex] ? "Next activity →" :
                      currentFairIndex === 0 ? "Find both real records first" :
                      currentFairIndex === 1 ? "Confirm the fair-test setup first" : "Record your Bridge 2 result first")}
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text style={{ color: fairTestStatus.explained ? c.success : c.muted, fontWeight: "700", flex: 1 }}>
                  {t(fairTestStatus.explained ? "Your comparison is recorded. Use the phase button below to continue." :
                    "Use both real results to explain what the test showed.")}
                </Text>
              )}
            </View>
          </View>
        </View>
      ) : focusLayout ? (
        <View style={{ marginTop: 12 }}>
          {focusLayout.safetyReminder ? (
            <View style={{ borderWidth: 1, borderColor: c.mysteryBorder, backgroundColor: c.mysterySoft, borderRadius: 10, padding: 10, marginBottom: 5 }}>
              <Text style={{ color: c.text, fontWeight: "700", fontSize: 13 }}>
                {t(focusLayout.safetyReminder)}
              </Text>
            </View>
          ) : null}
          {focusLayout.groups.map((group, index) => {
            const active = index === focusLayout.activeIndex;
            const expanded = active || Boolean(reviewGroups[`${stepId}:${group.id}`]);
            const finished = group.complete;
            const earlier = index < focusLayout.activeIndex;
            return (
              <View
                key={`${stepId}:${group.id}`}
                style={{
                  marginTop: 10,
                  borderWidth: 1,
                  borderColor: active ? c.accent : c.border,
                  borderRadius: 14,
                  padding: 12,
                  backgroundColor: active ? c.accentSoft : c.panel,
                }}
              >
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel={`${group.title}. ${group.optional ? "Optional challenge" : active ? "Current investigation" : finished ? "Earlier work" : earlier ? "Earlier activity" : "Coming up"}. ${expanded ? "Expanded" : "Collapsed"}`}
                  accessibilityState={{ expanded }}
                  disabled={active}
                  onPress={() => toggleReviewGroup(group.id)}
                  style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 10, paddingVertical: 5 }}
                >
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 11, fontWeight: "800", color: active ? c.accent : c.muted }}>
                      {t(group.optional ? "OPTIONAL CHALLENGE" : active ? "CURRENT INVESTIGATION" : finished ? "EARLIER DISCOVERIES" : earlier ? "EARLIER ACTIVITY" : "COMING UP")}
                    </Text>
                    <Text style={{ color: c.text, fontSize: 19, fontWeight: "900", marginTop: 4 }}>
                      {t(group.title)}
                    </Text>
                    {active ? (
                      <Text style={{ color: c.muted, fontSize: 14, lineHeight: 20, marginTop: 4 }}>
                        {t(group.prompt)}
                      </Text>
                    ) : null}
                    {group.total > 0 ? (
                      <Text style={{ color: c.muted, fontSize: 12, marginTop: 4 }}>
                        {group.completed} {t("of")} {group.total} {t("required activities recorded")}
                      </Text>
                    ) : null}
                  </View>
                  {!active ? (
                    <Text style={{ color: c.accent, fontWeight: "800", fontSize: 13 }}>
                      {t(expanded ? "Hide −" : group.optional ? "Explore +" : "Review +")}
                    </Text>
                  ) : null}
                </TouchableOpacity>
                {expanded ? group.blocks.map((block) => renderJourneyBlock({ ...block, self_guided_guidance: null,
                instructional_mode: null })) : null}
              </View>
            );
          })}
        </View>
      ) : visibleBlocks
        .filter((block) => block.type !== "story_prelude")
        .map(renderJourneyBlock)}
    </View>
    </MissionFocusedContext.Provider>
    </JourneyTContext.Provider>
  );
}
